import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Pendencias } from '../Pendencias';
import { ErroApi, mensagemDoErro } from '../api';
import {
  corrigirLinha, descartarLlPonto, descartarLpDeterminacao,
  descartarUmidadeDeterminacao, gravarInterpretacaoLl,
  gravarLlCabecalho, gravarLlPontos, gravarLpCabecalho, gravarLpDeterminacoes,
  gravarUmidadeCabecalho, gravarUmidadeDeterminacoes, pesarSeca,
  verEstufa, verLimiteLiquidez, verLimitePlasticidade, verUmidade,
} from '../api/triviais';
import type { BalancaDoEnsaio } from '../api/triviais';
import {
  AncoraDePendencias, BlocoDaFicha, CabecalhoDaFicha, EscolhaDeBalanca,
  EtapasDaFicha, FaixaDaFicha, JanelaDeDescarte, LaudoDaFicha, RodapeDeEtapas,
  fichaCongelada, useEquipamentos, useEtapa, useFicha,
} from '../Ficha';
import type { EquipamentoDaLista, Etapa, Indicador } from '../Ficha';
import { numero, paraCampo, paraNumero, plural, rotulo } from '../formato';
import { FotosDoRegistro } from '../FotosDoRegistro';
import { Alca, Plano, Reta, Segmento, Zoom, useJanela } from '../Grafico';
import type { Ponto } from '../Grafico';
import { useSessao } from '../sessao';
import {
  Area, Aviso, Campo, CampoNumerico, Carregando, Dialogo, ErroDaApi, Escolha,
  EscolhaPorCodigo, RespostaDaAcao, Selo, TabelaDeLancamento, comoCodigo,
} from '../ui';
import type { ColunaDeLancamento, LinhaDeLancamento, Opcao } from '../ui';
import { SeloDaEstufa, useAlertaDaEstufa } from './alertaDaEstufa';
import { daApi, nomeDaAmostra } from '../NomeDaAmostra';
import { PontosDoLl, RepeticaoLl } from './repeticao/RepeticaoLl';
import { TentativasAnteriores } from './repeticao/TentativasAnteriores';
import { TrilhaDoEnsaio } from './repeticao/TrilhaDoEnsaio';
import { useLinhasDaRepeticao } from './repeticao/Repeticao';
import {
  AvisoDoNp, COLUNAS_DE_CAPSULA, RepeticaoDeCapsulas, ResultadoVigente, linhaDeCapsula,
} from './repeticao/RepeticaoNumerica';
import type { ResultadoNumerico } from './repeticao/RepeticaoNumerica';

/**
 * Lançar os ensaios triviais de caracterização — umidade, LL e LP.
 *
 * ─── Em dois dias (onda 290) ───────────────────────────────────────────
 *
 * «Os dados iniciais são a quantidade de golpes e o número da
 *  cápsula/massa da cápsula+solo úmido. A massa da cápsula+solo seco é
 *  feita 24h depois, ou seja, deveria ser uma aba subsequente. Isso vale
 *  para todos os ensaios que medem a umidade: umidade, limites.»
 *  — Humberto, 06/10/2026.
 *
 * As três fichas têm agora as mesmas etapas de bancada:
 *
 *   1 · Condições       — o aparelho, a balança, a preparação;
 *   2 · Pesagem úmida   — cápsula, golpes (LL) e massa úmida. Cada linha
 *                         grava sozinha e TRAVA (RN-236): quem lançou
 *                         corrige por alguns minutos; depois, só quem tem
 *                         a permissão de corrigir, com motivo;
 *   3 · Pesagem seca    — no dia seguinte, só a massa seca; o w aparece
 *                         ao gravar a linha. A mesma pesagem entra também
 *                         pela tela Bancada › Estufa — a rota é a mesma e o
 *                         servidor recusa a duplicata (RN-235);
 *   4 · Interpretação   — SÓ no LL e SÓ para quem tem `ensaio.interpretar`
 *                         (RN-238): a reta proposta, as alças, o ponto
 *                         deslocado ou fora do ajuste, a tabela auxiliar;
 *   · Fotos e laudo.
 *
 * ─── As casas decimais são as da norma ─────────────────────────────────
 *
 *   · teor de umidade com 0,1 % — NBR 6457:2024, 4.4.1;
 *   · LL em número inteiro — NBR 6459:2025, 5.1;
 *   · LP em número inteiro e IP = LL − LP dos valores já expressos —
 *     NBR 7180:2025, 5.1.2 e 5.2.1.
 * O w de CADA cápsula continua com duas casas: é dado intermediário, e é
 * nele que a pesagem trocada aparece (70).
 *
 * ─── O que a tela não faz ──────────────────────────────────────────────
 *
 * Não calcula o resultado (D-29) — a reta que a interpretação desenha
 * enquanto se arrasta é a mesma conta do banco, refeita só para mostrar, e
 * o número que vale volta do servidor ao gravar. Não apaga (restrição 25).
 * E não decide quem pode corrigir: a tela só oferece o botão; quem recusa
 * é o gatilho da 0244.
 */

const CAT_CAPSULA = ['capsula'];
const CAT_CASAGRANDE = ['casagrande'];

/** As casas que a norma manda para o resultado (ver o topo do arquivo). */
const CASAS = { umidade: 1, wCapsula: 2, limite: 0 } as const;

const num = (v: unknown, casas: number) => numero(v, casas);

type Tipo = 'umidade' | 'limite-liquidez' | 'limite-plasticidade';

const descartada = (l: any) => Boolean(l.descartada || l.descartado);
const naEstufa = (l: any) => !descartada(l) && l.massa_capsula_solo_seco == null;
const FUSO = 'America/Sao_Paulo';

/** «07/10 09:38» — a hora curta da bancada. */
const quando = (v: unknown) => {
  if (!v) return '—';
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit' }).replace(',', '');
};
const hora = (ms: number) => new Date(ms).toLocaleTimeString('pt-BR',
  { timeZone: FUSO, hour: '2-digit', minute: '2-digit' });

/** «18 h 33» — quanto tempo a cápsula ficou (ou está) na estufa. */
export function tempoDeEstufa(desde: unknown, ate: number | string | null) {
  if (!desde) return '—';
  const a = Date.parse(String(desde));
  const b = typeof ate === 'number' ? ate : ate ? Date.parse(ate) : Date.now();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return '—';
  const min = Math.round((b - a) / 60000);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`;
}

/**
 * O relógio das janelas de correção, acertado pelo servidor.
 *
 * A janela «corrigir até 09:53» é contada pelo banco; o relógio do tablet
 * pode estar adiantado. A diferença entre os dois vem de `agora` em toda
 * resposta, e a tela anda sozinha a cada 20 s — o botão some quando a
 * janela fecha, sem precisar recarregar.
 */
export function useRelogio(agoraDoServidor?: string) {
  const [desvio, setDesvio] = useState(0);
  const [, setTique] = useState(0);
  useEffect(() => {
    if (agoraDoServidor) setDesvio(Date.parse(agoraDoServidor) - Date.now());
  }, [agoraDoServidor]);
  useEffect(() => {
    const t = window.setInterval(() => setTique((n) => n + 1), 20000);
    return () => window.clearInterval(t);
  }, []);
  return Date.now() + desvio;
}

// ══ quem pode corrigir o quê (a tela só OFERECE; o banco decide) ══════

export interface Correcao {
  grupo: 'umida' | 'seca';
  /** `janela` = o próprio autor, sem motivo; `travado` = permissão + motivo. */
  modo: 'janela' | 'travado';
  ate?: number;
}

export function correcaoPossivel(
  l: any, grupo: 'umida' | 'seca', euId: string | undefined,
  podeTravado: boolean, agora: number,
): Correcao | null {
  if (descartada(l)) return null;
  if (grupo === 'umida') {
    const ate = Date.parse(l.umido_corrigivel_ate);
    if (l.massa_capsula_solo_seco == null && euId && l.umido_por === euId
        && agora < ate) {
      return { grupo, modo: 'janela', ate };
    }
  } else {
    if (l.massa_capsula_solo_seco == null) return null;
    const ate = Date.parse(l.seco_corrigivel_ate);
    if (euId && l.seco_por === euId && agora < ate) {
      return { grupo, modo: 'janela', ate };
    }
  }
  return podeTravado ? { grupo, modo: 'travado' } : null;
}

// ══ o arreio comum das três telas ═════════════════════════════════════

function useEnsaioTrivial<T extends { pendencias: string[] }>(
  id: string, buscar: (id: string) => Promise<T>,
) {
  const [d, setD] = useState<T | null>(null);
  const equip = useEquipamentos();
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);

  const carregar = useCallback(async () => {
    try { setD(await buscar(id)); setVersao((v) => v + 1); } catch (e) { setErro(e); }
  }, [id, buscar]);

  useEffect(() => { void carregar(); }, [carregar]);

  /**
   * Toda gravação passa por aqui, e recebe a CHAMADA pronta.
   *
   * Os caminhos moram em `api/triviais.ts`, escritos por extenso: é o que
   * torna a rota localizável por quem procura no projeto, e visível para o
   * teste `26`, que confere se toda rota de criação tem caminho na tela.
   * Gravações que não devolvem o ensaio (a pesagem seca, a correção, a
   * interpretação) recarregam a ficha em seguida.
   */
  const gravar = useCallback(async (
    marca: string, chamada: () => Promise<unknown>, recarregar = false,
  ) => {
    setSalvando(marca); setErro(null); setOk(null);
    try {
      const r = await chamada();
      if (recarregar) setD(await buscar(id)); else setD(r as T);
      setOk('Gravado.');
      setVersao((v) => v + 1);
      return true;
    } catch (e) { setErro(e); return false; }
    finally { setSalvando(null); }
  }, [id, buscar]);

  const opcoes = (cats: string[]): Opcao[] => equip
    .filter((e) => cats.includes(e.categoria))
    .map((e) => ({ valor: e.id, texto: e.codigo_patrimonial, detalhe: e.nome }));

  const campoErro = (c: string) =>
    erro instanceof ErroApi ? erro.campo(c) : undefined;

  return { d, erro, ok, salvando, carregar, gravar, opcoes, campoErro,
    equip, versao, setErro };
}

/** O que «Gravado» quer dizer no rodapé das etapas. */
const notaDoRodape = (ok: string | null) =>
  ok ? 'Gravado — o resultado acima já é o do servidor.' : undefined;

/**
 * As cápsulas que estão na estufa agora, de qualquer ensaio (RN-237).
 *
 * A pesagem úmida mostra, na lista de cápsulas, qual está ocupada — quem
 * digita «41» vê «na estufa · LL 2609359-03» antes de gravar, e não só a
 * recusa do servidor depois.
 */
function useCapsulasNaEstufa(versao: number) {
  const [ocupadas, setOcupadas] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    let vivo = true;
    void verEstufa().then((e) => {
      if (!vivo) return;
      setOcupadas(new Map(e.naEstufa.map((l: any) => [
        l.capsula_equipamento_id,
        `na estufa · ${l.sigla} ${nomeDaAmostra(daApi(l, true))}`])));
    }).catch(() => { /* sem a marca; o servidor continua recusando */ });
    return () => { vivo = false; };
  }, [versao]);
  return ocupadas;
}

// ══ a janela de correção ══════════════════════════════════════════════

export function JanelaDeCorrecao({ alvo, tipo, opcoesCapsula, aoFechar, aoGravado }: {
  alvo: { linha: any; c: Correcao } | null;
  /** O tipo da linha — o dos triviais ou, desde a 294, o de qualquer ficha. */
  tipo: Tipo | string;
  opcoesCapsula: Opcao[];
  aoFechar(): void;
  aoGravado(): void;
}) {
  const [cap, setCap] = useState('');
  const [golpes, setGolpes] = useState('');
  const [umido, setUmido] = useState('');
  const [seco, setSeco] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [gravando, setGravando] = useState(false);
  const l = alvo?.linha;
  useEffect(() => {
    if (!l) return;
    setCap(l.capsula_equipamento_id ?? '');
    setGolpes(l.golpes != null ? String(l.golpes) : '');
    setUmido(paraCampo(l.massa_capsula_solo_umido));
    setSeco(l.massa_capsula_solo_seco != null ? paraCampo(l.massa_capsula_solo_seco) : '');
    setMotivo(''); setErro(null);
  }, [l]);
  if (!alvo) return <Dialogo aberto={false} titulo="" aoFechar={aoFechar}><span /></Dialogo>;
  const { c } = alvo;
  const exigeMotivo = c.modo === 'travado';
  const comGolpes = tipo === 'limite-liquidez';
  /* A linha da Estufa já traz o nome dela («a umidade final 1 do CP 2»). */
  const rotuloLinha = l.rotulo_linha
    ? `${String(l.rotulo_linha)[0].toUpperCase()}${String(l.rotulo_linha).slice(1)}`
    : comGolpes ? `Ponto ${l.ordem}` : `Determinação ${l.ordem}`;

  const mudancas: Record<string, unknown> = {};
  if (c.grupo === 'umida') {
    if (cap && cap !== l.capsula_equipamento_id) mudancas.capsulaEquipamentoId = cap;
    const g = paraNumero(golpes);
    if (comGolpes && g != null && g !== Number(l.golpes)) mudancas.golpes = Math.round(g);
    const u = paraNumero(umido);
    if (u != null && Math.abs(u - Number(l.massa_capsula_solo_umido)) > 1e-9) {
      mudancas.massaCapsulaSoloUmido = u;
    }
  } else {
    const s = paraNumero(seco);
    if (s != null && Math.abs(s - Number(l.massa_capsula_solo_seco)) > 1e-9) {
      mudancas.massaCapsulaSoloSeco = s;
    }
  }
  const falta = !Object.keys(mudancas).length ? 'Nada mudou ainda.'
    : exigeMotivo && motivo.trim().length < 5
      ? 'Escreva o motivo — pelo menos 5 caracteres.' : null;

  const gravar = async () => {
    if (falta || gravando) return;
    setGravando(true); setErro(null);
    try {
      await corrigirLinha({ linhaId: l.id, ...mudancas,
        ...(motivo.trim() ? { motivo: motivo.trim() } : {}) });
      aoGravado(); aoFechar();
    } catch (e) { setErro(e); } finally { setGravando(false); }
  };

  return (
    <Dialogo aberto titulo={`Corrigir a pesagem ${c.grupo === 'umida' ? 'úmida' : 'seca'}`}
      rotulo={`${rotuloLinha} · ${l.capsula}`} aoFechar={aoFechar}>
      <p className="sub">
        {exigeMotivo
          ? <>A linha está travada desde {quando(c.grupo === 'umida' ? l.umido_em : l.seco_em)}.
            Você tem a permissão «Corrigir lançamento travado»: a correção passa, e o
            valor anterior fica na trilha com o motivo (RN-236).</>
          : <>Você lançou esta pesagem e pode corrigi-la até {hora(c.ate ?? Date.now())}.
            Depois disso ela trava.</>}
      </p>
      <div className="fe-campos dd-correcao">
        {c.grupo === 'umida' ? (
          <>
            <EscolhaPorCodigo rotulo="Cápsula" opcoes={opcoesCapsula.map(comoCodigo)}
              valor={cap} aoEscolher={setCap}
              dica={l.massa_capsula_solo_seco != null
                ? 'Já pesada seca: trocar a cápsula muda a tara.' : undefined} />
            {comGolpes && (
              <CampoNumerico rotulo="Golpes" inteiro value={golpes}
                onChange={(e) => setGolpes(e.target.value)}
                dica={`gravado: ${l.golpes}`} />
            )}
            <CampoNumerico rotulo="Cápsula + solo úmido (g)" value={umido}
              onChange={(e) => setUmido(e.target.value)}
              dica={`gravado: ${num(l.massa_capsula_solo_umido, 2)}`} />
          </>
        ) : (
          <CampoNumerico rotulo="Cápsula + solo seco (g)" value={seco}
            onChange={(e) => setSeco(e.target.value)}
            dica={`gravado: ${num(l.massa_capsula_solo_seco, 2)}`} />
        )}
        {exigeMotivo && (
          <Area rotulo="Motivo da correção" rows={2} value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            dica="Fica na trilha e aparece para quem verifica." />
        )}
      </div>
      <RespostaDaAcao erro={erro} ok={null} />
      <div className="rodape-dialogo">
        <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="botao" aria-disabled={falta !== null || gravando}
          title={falta ?? undefined} onClick={() => void gravar()}>
          {gravando ? 'Gravando…' : 'Gravar correção'}
        </button>
      </div>
    </Dialogo>
  );
}

// ══ 2 · a pesagem úmida ═══════════════════════════════════════════════

interface LinhaNova { cap: string; golpes: string; umido: string; seco: string }
const linhaNova = (): LinhaNova => ({ cap: '', golpes: '', umido: '', seco: '' });

interface PesagemUmidaProps {
  tipo: Tipo;
  linhas: any[];
  opcoesCapsula: Opcao[];
  equipamentos: EquipamentoDaLista[];
  balanca: BalancaDoEnsaio | null;
  salvando: string | null;
  minimo: number;
  euId?: string;
  podeTravado: boolean;
  agora: number;
  ocupadas: Map<string, string>;
  erroBalanca?: string;
  aoGravar(corpo: { balancaEquipamentoId?: string; linhas: any[] }): Promise<boolean>;
  aoDescartar(linhaId: string, motivo: string): Promise<boolean>;
  aoCorrigir(alvo: { linha: any; c: Correcao }): void;
}

/**
 * A pesagem úmida: cada linha grava sozinha, e a partir daí é a cápsula
 * na estufa — travada (RN-236).
 */
export function PesagemUmida({
  tipo, linhas, opcoesCapsula, equipamentos, balanca, salvando, minimo,
  euId, podeTravado, agora, ocupadas, erroBalanca, aoGravar, aoDescartar,
  aoCorrigir,
}: PesagemUmidaProps) {
  const comGolpes = tipo === 'limite-liquidez';
  const rotuloLinha = comGolpes ? 'Ponto' : 'Determinação';
  const validas = linhas.filter((l) => !descartada(l)).length;
  const [novas, setNovas] = useState<LinhaNova[]>(
    () => Array.from({ length: Math.max(1, minimo - validas) }, linhaNova));
  const [colando, setColando] = useState(false);
  const [colagem, setColagem] = useState('');
  const [descartando, setDescartando] = useState<any | null>(null);
  const [balancaId, setBalancaId] = useState(balanca?.id ?? '');
  useEffect(() => { setBalancaId(balanca?.id ?? ''); }, [balanca?.id]);

  const opcoes = opcoesCapsula.map((o) => {
    const oc = ocupadas.get(o.valor);
    return comoCodigo(oc ? { ...o, detalhe: `${oc} — ${o.detalhe ?? ''}` } : o);
  });

  const mexer = (i: number, campo: keyof LinhaNova, v: string) =>
    setNovas((ls) => ls.map((l, j) => (j === i ? { ...l, [campo]: v } : l)));

  const pronta = (l: LinhaNova) => Boolean(l.cap && l.umido && (!comGolpes || l.golpes));
  const faltaNa = (l: LinhaNova): string | null => {
    if (!balancaId) return 'Falta escolher a balança.';
    if (!l.cap) return 'Falta escolher a cápsula.';
    if (comGolpes && !l.golpes) return 'Faltam os golpes.';
    if (!l.umido) return 'Falta a massa de cápsula + solo úmido.';
    return null;
  };

  const base = linhas.reduce((m: number, l: any) => Math.max(m, l.ordem), 0);
  const corpoDa = (l: LinhaNova, ordem: number) => ({
    ordem,
    capsulaEquipamentoId: l.cap,
    massaCapsulaSoloUmido: paraNumero(l.umido),
    ...(l.seco ? { massaCapsulaSoloSeco: paraNumero(l.seco) } : {}),
    ...(comGolpes ? { golpes: Math.round(Number(paraNumero(l.golpes) ?? 0)) } : {}),
  });

  /* A cápsula gravada sem a massa seca vai para a estufa: o número da barra
     (308) relê (Ruling R33), uma vez por gravação. Com a seca colada junto
     (a planilha de um dia anterior), ela não entra. */
  const avisarSeEntrou = (corpos: Array<{ massaCapsulaSoloSeco?: number | null }>) => {
    if (corpos.some((c) => c.massaCapsulaSoloSeco == null)) {
      window.dispatchEvent(new CustomEvent('labmanager:estufa-mudou'));
    }
  };

  /* Descartar uma linha que estava na estufa tira a cápsula de lá (R33). */
  const descartar = async (alvo: any, motivo: string) => {
    const ok = await aoDescartar(alvo.id, motivo);
    if (ok && naEstufa(alvo)) window.dispatchEvent(new CustomEvent('labmanager:estufa-mudou'));
    return ok;
  };

  const gravarUma = async (i: number) => {
    const l = novas[i];
    if (faltaNa(l) || salvando !== null) return;
    const corpo = corpoDa(l, base + 1);
    const ok = await aoGravar({
      ...(balancaId ? { balancaEquipamentoId: balancaId } : {}),
      linhas: [corpo],
    });
    if (ok) {
      avisarSeEntrou([corpo]);
      setNovas((ls) => {
        const resto = ls.filter((_, j) => j !== i);
        return resto.length ? resto : [linhaNova()];
      });
    }
  };

  const prontas = novas.filter(pronta);
  const faltaVisivel = novas
    .map((l, i) => ({ i, comecada: Boolean(l.cap || l.golpes || l.umido), falta: faltaNa(l) }))
    .find((x) => x.comecada && x.falta !== null) as { i: number; falta: string } | undefined;
  const gravarTodas = async () => {
    if (!balancaId || !prontas.length || salvando !== null) return;
    const corpos = prontas.map((l, k) => corpoDa(l, base + k + 1));
    const ok = await aoGravar({
      balancaEquipamentoId: balancaId,
      linhas: corpos,
    });
    if (ok) {
      avisarSeEntrou(corpos);
      const resto = novas.filter((l) => !pronta(l));
      setNovas(resto.length ? resto : [linhaNova()]);
    }
  };

  /**
   * A colagem: uma linha por cápsula, na ordem da tela. LL: `golpes;
   * cápsula; úmido[; seco]` — a ordem da bancada, desde o acerto da 290
   * («seria melhor colocar na sequência de execução: número de golpes,
   * número da cápsula, massa cápsula + solo», Humberto, 07/10/2026); a
   * ordem antiga (`cápsula; golpes; …`) continua sendo lida: quem decide
   * é a coluna que casa com uma cápsula. Umidade e LP: `cápsula;
   * úmido[; seco]`. Tabulação, ponto e vírgula ou barra separam colunas —
   * a vírgula é a decimal. A massa seca colada (planilha de um dia
   * anterior) entra junto e o banco carimba as duas pesagens com a mesma
   * hora.
   */
  const aplicarColagem = () => {
    const lidas: LinhaNova[] = [];
    const capsulaDe = (t: string) =>
      opcoesCapsula.find((o) => o.texto.toLowerCase() === t.toLowerCase());
    for (const t of colagem.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)) {
      const p = t.split(/[\t;|]+/).map((s) => s.trim());
      if (p.length < (comGolpes ? 3 : 2)) continue;
      if (comGolpes) {
        const antiga = !capsulaDe(p[1]) && Boolean(capsulaDe(p[0]));
        const [golpes, codigo] = antiga ? [p[1], p[0]] : [p[0], p[1]];
        lidas.push({ cap: capsulaDe(codigo)?.valor ?? '', golpes, umido: p[2], seco: p[3] ?? '' });
      } else {
        lidas.push({ cap: capsulaDe(p[0])?.valor ?? '', golpes: '', umido: p[1], seco: p[2] ?? '' });
      }
    }
    if (lidas.length) { setNovas(lidas); setColagem(''); setColando(false); }
  };

  /* A ordem da bancada (acerto 290): no LL, golpes → cápsula → massa
     úmida — é também a ordem do Tab na linha nova. A tara é da cápsula
     e fica ao lado dela; não recebe foco. */
  const colunas: ColunaDeLancamento[] = [
    { texto: comGolpes ? 'Ponto' : 'Nº' },
    ...(comGolpes ? [{ texto: 'Golpes', num: true }] : []),
    { texto: 'Cápsula' },
    { texto: 'Tara (g)', num: true },
    { texto: 'Cáps. + solo úmido (g)', num: true },
    { texto: 'Na estufa desde' },
    { texto: '' },
  ];
  const iCapsula = comGolpes ? 2 : 1;
  const iTara = comGolpes ? 3 : 2;

  const lancadas: LinhaDeLancamento[] = linhas.map((l) => {
    const morta = descartada(l);
    const corr = correcaoPossivel(l, 'umida', euId, podeTravado, agora);
    const situacao = morta
      ? <Selo tom="neutro">Descartada</Selo>
      : l.massa_capsula_solo_seco == null
        ? <>{quando(l.umido_em)}<span className="dd-quem">{l.umido_por_nome ?? ''}</span></>
        : <>pesada seca {quando(l.seco_em)}</>;
    return {
      chave: l.id,
      classe: morta ? 'descartada' : corr?.modo === 'janela' ? 'dd-fresca' : undefined,
      titulo: <>{rotuloLinha} {l.ordem} · <span className="cod">{l.capsula}</span></>,
      resultado: comGolpes ? <span className="n">{l.golpes} golpes</span> : undefined,
      nota: morta ? `Descartada: ${l.motivo_descarte ?? ''}` : undefined,
      semCartao: [0, iCapsula],
      celulas: [
        l.ordem,
        ...(comGolpes ? [<>
          {l.golpes}
          {l.fora_da_faixa_normativa && !morta ? <Selo tom="atencao">Fora de 15–35</Selo> : null}
        </>] : []),
        <span className="cod">{l.capsula}</span>,
        l.tara == null ? <Selo tom="critico">Sem calibração interna</Selo> : num(l.tara, 2),
        num(l.massa_capsula_solo_umido, 2),
        situacao,
        morta ? null : (
          <span className="dd-acoes">
            {corr
              ? <button type="button" className="botao secundario pequeno"
                  disabled={salvando !== null}
                  onClick={() => aoCorrigir({ linha: l, c: corr })}>
                  {corr.modo === 'janela' ? `Corrigir · até ${hora(corr.ate!)}` : 'Corrigir'}
                </button>
              : <span className="dd-trava" title="Linha gravada trava (RN-236)">travada</span>}
            <button type="button" className="botao discreto pequeno"
              aria-label={`Descartar ${rotuloLinha.toLowerCase()} ${l.ordem}`}
              disabled={salvando !== null} onClick={() => setDescartando(l)}>
              Descartar
            </button>
          </span>
        ),
      ],
    };
  });

  const novasLinhas: LinhaDeLancamento[] = novas.map((l, i) => {
    const falta = faltaNa(l);
    return {
      chave: `nova-${i}`,
      classe: 'nova',
      titulo: `${rotuloLinha} ${base + i + 1} · nova`,
      semCartao: [0, iTara],
      celulas: [
        <span className="sub">{base + i + 1}</span>,
        ...(comGolpes ? [(
          <CampoNumerico rotulo="Golpes" rotuloOculto inteiro value={l.golpes}
            onChange={(e) => mexer(i, 'golpes', e.target.value)} />
        )] : []),
        <EscolhaPorCodigo rotulo="" opcoes={opcoes} valor={l.cap}
          aoEscolher={(v) => mexer(i, 'cap', v)} />,
        <span className="sub">—</span>,
        <CampoNumerico rotulo="Cápsula + solo úmido (g)" rotuloOculto value={l.umido}
          onChange={(e) => mexer(i, 'umido', e.target.value)} />,
        <span className="sub">{l.seco ? `seca colada: ${l.seco}` : 'ao gravar'}</span>,
        <span className="dd-acoes">
          <button type="button" className="botao pequeno fe-gravar-linha" data-gravar-linha=""
            aria-disabled={falta !== null || salvando !== null}
            title={falta ?? 'Grava esta linha (Enter)'}
            onClick={() => void gravarUma(i)}>
            {salvando === 'linhas' ? 'Gravando…' : `Gravar ${comGolpes ? 'ponto' : 'linha'}`}
          </button>
          <button type="button" className="remover" tabIndex={-1}
            aria-label={`Remover a linha nova ${base + i + 1}`}
            title="Remover esta linha (ainda não gravada)"
            onClick={() => setNovas((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : [linhaNova()]))}>
            ×
          </button>
        </span>,
      ],
    };
  });

  const plural2 = comGolpes ? 'Pontos' : 'Determinações';
  return (
    <BlocoDaFicha
      titulo="Pesagem úmida"
      selo={validas > 0
        ? <Selo tom={validas >= minimo ? 'ok' : 'atencao'}>{validas} de {minimo}</Selo>
        : undefined}
      sub={comGolpes
        ? 'Um ponto por cápsula: os golpes que fecharam a ranhura e a massa da cápsula com o solo úmido. Cada linha grava sozinha e vai para a estufa — a partir daí ela trava.'
        : 'Uma linha por cápsula: a massa da cápsula com o solo úmido. Cada linha grava sozinha e vai para a estufa — a partir daí ela trava.'}>
      <div className="fe-miolo fe-campos">
        <EscolhaDeBalanca rotulo="Balança da pesagem úmida" equipamentos={equipamentos}
          valor={balancaId} aoEscolher={setBalancaId} erro={erroBalanca} />
      </div>
      <div className="fe-tabela">
        <div className="fe-tabela-topo">
          <h3>{plural2} <small>{comGolpes
            ? 'cubra a faixa de 15 a 35 golpes; mínimo de cinco pontos'
            : minimo > 1 ? `mínimo de ${minimo}` : ''}</small></h3>
          <button type="button" className="botao discreto pequeno"
            aria-expanded={colando} onClick={() => setColando((v) => !v)}>
            Colar da planilha
          </button>
          <button type="button" className="botao discreto pequeno"
            onClick={() => setNovas((ls) => [...ls, linhaNova()])}>
            + linha
          </button>
        </div>
        {colando && (
          <div className="colagem">
            <textarea rows={3} aria-label="Colar da planilha"
              placeholder={comGolpes
                ? 'Uma linha por ponto (golpes, cápsula, úmido):\n25\tCAP-07\t22,30'
                : 'Uma linha por cápsula:\nCAP-07\t22,30'}
              value={colagem} onChange={(e) => setColagem(e.target.value)} />
            <button type="button" className="botao secundario"
              onClick={aplicarColagem} disabled={!colagem.trim()}>Ler a colagem</button>
          </div>
        )}
        <TabelaDeLancamento rotulo={`${plural2} — lançados e novos`}
          classe="capsulas fe-lanc dd-umida" colunas={colunas}
          linhas={[...lancadas, ...novasLinhas]} />
        <div className="acoes acoes-fixas fe-acoes">
          {/* O «Gravar» da linha diz o que espera (achado A3, 168.60): a
              primeira linha começada e incompleta aparece aqui, por extenso. */}
          {faltaVisivel
            ? <p className="o-que-falta">
                {rotuloLinha} {base + faltaVisivel.i + 1}: {faltaVisivel.falta}
              </p>
            : <p className="fe-lembrete">
                A cápsula que está na estufa não aceita outra linha até ser pesada seca (RN-237).
              </p>}
          {prontas.length > 1 && (
            <button type="button" className="principal"
              aria-disabled={!balancaId || salvando !== null}
              onClick={() => void gravarTodas()}>
              Gravar as {prontas.length} linhas
            </button>
          )}
        </div>
      </div>
      {opcoesCapsula.length === 0 && (
        <Aviso tipo="atencao">
          Não há cápsula com calibração interna cadastrada. Sem a tara calibrada o sistema não
          calcula umidade — e não vai inventar uma.
        </Aviso>
      )}
      <JanelaDeDescarte
        alvo={descartando ? `${rotuloLinha.toLowerCase()} ${descartando.ordem} · ${descartando.capsula ?? ''}` : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={(motivo) => descartar(descartando, motivo)}
        salvando={salvando !== null} />
    </BlocoDaFicha>
  );
}

// ══ 3 · a pesagem seca ════════════════════════════════════════════════

interface PesagemSecaProps {
  tipo: Tipo;
  linhas: any[];
  equipamentos: EquipamentoDaLista[];
  balanca: BalancaDoEnsaio | null;
  euId?: string;
  podeTravado: boolean;
  agora: number;
  aoPesado(): Promise<void>;
  aoCorrigir(alvo: { linha: any; c: Correcao }): void;
  /** Fecha a etapa em média/depuração (umidade, LP): o rodapé da tabela. */
  rodape?: React.ReactNode;
  foraDaMedia?: (l: any) => boolean;
}

/**
 * A pesagem seca (RN-235): só a massa seca, linha a linha.
 *
 * Grava pela MESMA rota da tela Estufa (`/estufa/pesagens`). Depois de
 * cada pesagem o servidor devolve o w no contexto do ensaio — o ponto do
 * LL que quebra a tendência, a determinação do LP fora dos 5 %, a
 * amplitude da umidade — e a tela mostra, para repesar enquanto a
 * cápsula ainda está na bancada.
 */
export function PesagemSeca({
  tipo, linhas, equipamentos, balanca, euId, podeTravado, agora, aoPesado,
  aoCorrigir, rodape, foraDaMedia,
}: PesagemSecaProps) {
  const comGolpes = tipo === 'limite-liquidez';
  const rotuloLinha = comGolpes ? 'Ponto' : 'Determinação';
  const [balancaId, setBalancaId] = useState(balanca?.id ?? '');
  useEffect(() => { if (!balancaId && balanca?.id) setBalancaId(balanca.id); }, [balanca?.id, balancaId]);
  const [valores, setValores] = useState<Record<string, string>>({});
  const [pesando, setPesando] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [aviso, setAviso] = useState<{ texto: string; alertas: string[] } | null>(null);
  /* RN-250 (onda 305): a cápsula que passou das horas do ajuste ganha o selo. */
  const alertaHoras = useAlertaDaEstufa();

  const vivas = linhas.filter((l) => !descartada(l));
  const pesadas = vivas.filter((l) => l.massa_capsula_solo_seco != null).length;
  const ordenadas = [...vivas].sort((a, b) =>
    (naEstufa(a) ? Date.parse(a.umido_em) : Infinity) - (naEstufa(b) ? Date.parse(b.umido_em) : Infinity)
    || a.ordem - b.ordem);

  const pesar = async (l: any) => {
    const massa = paraNumero(valores[l.id] ?? '');
    if (massa == null || pesando) return;
    if (!balancaId) { setErro(new ErroApi(400, 'Escolha a balança da pesagem seca (RN-215).')); return; }
    setPesando(l.id); setErro(null);
    try {
      const r = await pesarSeca({ linhaId: l.id, massaCapsulaSoloSeco: massa,
        balancaEquipamentoId: balancaId });
      setValores((v) => { const n = { ...v }; delete n[l.id]; return n; });
      setAviso({
        texto: `${l.capsula} pesada: ${num(massa, 2)} g → w ${num(r.linha.umidade, 2)} %`,
        alertas: r.contexto.alertas,
      });
      /* Uma cápsula a menos na estufa, como na tela Estufa: o número da
         barra (308) relê (Ruling R25). */
      window.dispatchEvent(new CustomEvent('labmanager:estufa-mudou'));
      await aoPesado();
      /* O foco vai para a próxima cápsula na estufa — o gesto seguinte. */
      window.setTimeout(() => {
        const prox = document.querySelector<HTMLInputElement>('.dd-seca input[data-seca]');
        prox?.focus();
      }, 60);
    } catch (e) { setErro(e); } finally { setPesando(null); }
  };

  const colunas: ColunaDeLancamento[] = [
    { texto: comGolpes ? 'Ponto' : 'Nº' },
    ...(comGolpes ? [{ texto: 'Golpes', num: true }] : []),
    { texto: 'Cápsula' },
    { texto: 'Tara (g)', num: true },
    { texto: 'Cáps. + solo úmido (g)', num: true },
    { texto: 'Na estufa', num: true },
    { texto: 'Cáps. + solo seco (g)', num: true },
    { texto: 'w (%)', num: true },
    { texto: '' },
  ];

  const linhasTab: LinhaDeLancamento[] = ordenadas.map((l) => {
    const pendente = naEstufa(l);
    const corr = pendente ? null : correcaoPossivel(l, 'seca', euId, podeTravado, agora);
    const fora = foraDaMedia?.(l) ?? false;
    return {
      chave: l.id,
      classe: pendente ? 'nova' : corr?.modo === 'janela' ? 'dd-fresca' : fora ? 'depurada' : undefined,
      titulo: <>{rotuloLinha} {l.ordem} · <span className="cod">{l.capsula}</span></>,
      resultado: l.umidade != null ? <span className="n">{num(l.umidade, CASAS.wCapsula)} %</span> : undefined,
      semCartao: [0, comGolpes ? 2 : 1],
      celulas: [
        l.ordem,
        ...(comGolpes ? [<span className="dd-travado">{l.golpes}</span>] : []),
        <span className="cod">{l.capsula}</span>,
        l.tara == null ? <Selo tom="critico">Sem calibração interna</Selo> : num(l.tara, 2),
        <span className="dd-travado">{num(l.massa_capsula_solo_umido, 2)}</span>,
        <>{tempoDeEstufa(l.umido_em, pendente ? agora : l.seco_em)}
          {pendente && <SeloDaEstufa desde={l.umido_em} agora={agora} horas={alertaHoras} />}</>,
        pendente
          ? <CampoNumerico rotulo={`Cápsula + solo seco — ${l.capsula}`} rotuloOculto
              data-seca="1" value={valores[l.id] ?? ''}
              disabled={pesando !== null}
              onChange={(e) => setValores((v) => ({ ...v, [l.id]: e.target.value }))} />
          : <>{num(l.massa_capsula_solo_seco, 2)}
              <span className="dd-quem">{quando(l.seco_em)} · {l.seco_por_nome ?? ''}</span></>,
        <b className="fe-w">{l.umidade != null ? num(l.umidade, CASAS.wCapsula) : '—'}
          {fora ? <Selo tom="atencao">Fora dos 5 %</Selo> : null}</b>,
        pendente
          ? <button type="button" className="botao pequeno fe-gravar-linha" data-gravar-linha=""
              aria-disabled={!valores[l.id] || pesando !== null}
              onClick={() => void pesar(l)}>
              {pesando === l.id ? 'Gravando…' : 'Gravar'}
            </button>
          : corr
            ? <button type="button" className="botao secundario pequeno"
                onClick={() => aoCorrigir({ linha: l, c: corr })}>
                {corr.modo === 'janela' ? `Corrigir · até ${hora(corr.ate!)}` : 'Corrigir'}
              </button>
            : <span className="dd-trava">travada</span>,
      ],
    };
  });

  return (
    <BlocoDaFicha titulo="Pesagem seca"
      selo={vivas.length ? <Selo tom={pesadas === vivas.length ? 'ok' : 'atencao'}>
        {pesadas} de {vivas.length}</Selo> : undefined}
      sub="Só a massa da cápsula com o solo seco. Cápsula, golpes e massa úmida vêm da pesagem úmida e não se editam aqui. O w aparece ao gravar a linha (Enter).">
      <div className="fe-miolo fe-campos">
        <EscolhaDeBalanca rotulo="Balança da pesagem seca" equipamentos={equipamentos}
          valor={balancaId} aoEscolher={setBalancaId} />
      </div>
      {aviso && (
        <Aviso tipo={aviso.alertas.length ? 'atencao' : 'ok'} titulo={aviso.texto}>
          {aviso.alertas.length
            ? <ul className="dd-alertas">{aviso.alertas.map((a) => <li key={a}>{a}</li>)}</ul>
            : 'Sem nada fora do esperado.'}
        </Aviso>
      )}
      <RespostaDaAcao erro={erro} ok={null} />
      <div className="fe-tabela">
        <div className="fe-tabela-topo">
          <h3>{comGolpes ? 'Pontos' : 'Determinações'} <small>a que está há mais tempo na estufa primeiro</small></h3>
          <Link className="botao discreto pequeno" to="/estufa">Pesar pela Estufa</Link>
        </div>
        {vivas.length === 0
          ? <p className="sub dd-vazio">Nada lançado na pesagem úmida ainda.</p>
          : <TabelaDeLancamento rotulo="Pesagem seca" classe="capsulas fe-lanc dd-seca"
              colunas={colunas} linhas={linhasTab} />}
        {rodape}
      </div>
    </BlocoDaFicha>
  );
}

// ══ pendências, fotos, laudo ══════════════════════════════════════════

function OQueFalta({ d, id, carregar }: {
  d: { pendencias: string[]; pendenciasDetalhadas?: any[] };
  id: string; carregar(): void;
}) {
  return (
    <AncoraDePendencias>
      <Pendencias itens={d.pendencias} id={id}
        detalhe={d.pendenciasDetalhadas} aoJustificar={carregar} />
    </AncoraDePendencias>
  );
}

function SoLeitura({ status }: { status?: string | null }) {
  if (!fichaCongelada(status)) return null;
  return (
    <Aviso tipo="info" titulo={`Ensaio ${rotulo(status).toLowerCase()} — só leitura`}>
      {status === 'aprovado'
        ? 'Dado de ensaio aprovado não se altera. Correção é nova revisão: peça a reabertura a quem aprova.'
        : 'Ensaio encerrado não recebe dado novo. Se o serviço for refeito, é ensaio novo.'}
    </Aviso>
  );
}

function FotosELaudo({ id, nota }: { id: string; nota?: string }) {
  return (
    <>
      <FotosDoRegistro id={id} titulo="Registro fotográfico"
        dica="A cápsula, o material, o aparelho montado — o que ajudar a conferir este ensaio depois." />
      <LaudoDaFicha id={id} nota={nota} />
    </>
  );
}

const NOTA_DO_PAR = 'O laudo de limites sai com LL e LP juntos — o '
  + 'índice de plasticidade só existe no par.';

const PREPARACAO: Opcao[] = [
  { valor: 'sem_secagem_previa', texto: 'sem secagem prévia' },
  { valor: 'com_secagem_previa', texto: 'com secagem prévia' },
];

/** O arreio comum da correção: a janela e quem a abre. */
function useCorrecao() {
  const [alvo, setAlvo] = useState<{ linha: any; c: Correcao } | null>(null);
  return { alvo, abrir: setAlvo, fechar: () => setAlvo(null) };
}

// ══ 1 · teor de umidade ═══════════════════════════════════════════════

/** A comparação da repetição da umidade: a média do servidor, com 0,1 %. */
const RESULTADO_UMIDADE: ResultadoNumerico = {
  rotulo: 'Teor de umidade', unidade: '%', casas: CASAS.umidade, unidadeDaDiferenca: 'p.p.',
  valor: (f) => (f?.cabecalho?.umidade_media != null ? Number(f.cabecalho.umidade_media) : null),
  nota: (f, lado) => {
    const n = Number(f?.cabecalho?.determinacoes ?? 0);
    const espera = Number(f?.cabecalho?.aguardando_seca ?? 0);
    return `${plural(n, 'determinação válida', 'determinações válidas')}`
      + (lado === 'atual' ? ' (mínimo 3)' : '') + (espera ? ` · ${espera} na estufa` : '');
  },
  semDiferenca: 'sai com a primeira determinação pesada seca',
};

export function Umidade() {
  const { id = '' } = useParams();
  const { eu, pode } = useSessao();
  const t = useEnsaioTrivial(id, verUmidade);
  const ficha = useFicha(id, 'NBR 6457', t.versao);
  const agora = useRelogio(t.d?.agora);
  const ocupadas = useCapsulasNaEstufa(t.versao);
  const corr = useCorrecao();
  /* R2 (onda 323): com a repetição aberta, as etapas de bancada listam e
     numeram as linhas DELA. */
  const rep = useLinhasDaRepeticao<any>(id, 'umidade', t.d?.repeticaoAberta, t.versao);
  const [fracao, setFracao] = useState<string | null>(null);
  const [temp, setTemp] = useState('');
  const [horas, setHoras] = useState('');
  const [balancaId, setBalancaId] = useState('');

  const c = t.d?.cabecalho;
  useEffect(() => {
    setFracao(c?.fracao ?? 'total');
    setTemp(c?.temperatura_secagem != null ? paraCampo(c.temperatura_secagem) : '');
    setHoras(c?.horas_estufa != null ? paraCampo(c.horas_estufa) : '');
  }, [c?.fracao, c?.temperatura_secagem, c?.horas_estufa]);
  useEffect(() => { setBalancaId(t.d?.balanca?.id ?? ''); }, [t.d?.balanca?.id]);

  const validas: number = c?.determinacoes ?? 0;
  const aguardando: number = c?.aguardando_seca ?? 0;
  const temCondicoes = c?.temperatura_secagem != null;
  const temUmida = validas >= 3 && !!t.d?.balanca;
  const temSeca = temUmida && aguardando === 0;
  /* A Interpretação (onda 323) existe só para quem interpreta: é onde se
     repete o ensaio e se comparam os dois teores. */
  const interpreta = pode('ensaio.interpretar');
  const total = interpreta ? 5 : 4;
  const [etapa, irPara] = useEtapa(total,
    !temCondicoes ? 1 : !temUmida ? 2 : !temSeca ? 3 : total);

  if (t.erro && !t.d) return <ErroDaApi erro={t.erro} />;
  if (!t.d) return <Carregando texto="Abrindo o ensaio" />;
  const d = t.d;
  const congelado = fichaCongelada(ficha?.status);
  const podeTravado = pode('ensaio.corrigir_lancamento');
  const pronto = validas >= 3 && aguardando === 0;
  const etapaLaudo = total;
  /* As linhas e os resumos das etapas de bancada: os da repetição aberta (R2). */
  const repAberta = d.repeticaoAberta != null;
  const linhasDaBancada: any[] = repAberta ? (rep.atual?.determinacoes ?? []) : d.determinacoes;
  const balancaDaBancada = repAberta ? (rep.atual?.balanca ?? d.balanca) : d.balanca;
  const ca = rep.atual?.cabecalho;
  const bValidas: number = repAberta ? (ca?.determinacoes ?? 0) : validas;
  const bPesadas: number = repAberta ? (ca?.pesadas ?? 0) : (c?.pesadas ?? 0);
  const bAguardando: number = repAberta ? (ca?.aguardando_seca ?? 0) : aguardando;
  const bUmida = repAberta ? bValidas >= 3 && !!balancaDaBancada : temUmida;
  const bSeca = repAberta ? bUmida && bAguardando === 0 : temSeca;
  const naTentativa = repAberta ? ` · tentativa ${d.repeticaoAberta}` : '';

  const etapas: Etapa[] = [
    { nome: 'Condições', feita: temCondicoes,
      resumo: temCondicoes
        ? [rotulo(c?.fracao === 'total' ? 'amostra total' : c?.fracao),
          c?.temperatura_secagem != null ? `${num(c.temperatura_secagem, 0)} °C` : null,
          balancaDaBancada?.codigo].filter(Boolean).join(' · ')
        : 'a gravar' },
    { nome: 'Pesagem úmida', feita: bUmida, falta: !balancaDaBancada && bValidas > 0,
      resumo: `${plural(bValidas, 'determinação', 'determinações')}${naTentativa}` },
    { nome: 'Pesagem seca', feita: bSeca,
      resumo: bValidas ? `${bPesadas} de ${bValidas} pesadas${naTentativa}` : 'depois da úmida' },
    ...(interpreta ? [{
      nome: 'Interpretação', feita: pronto && !repAberta,
      resumo: repAberta ? `repetindo · tentativa ${d.repeticaoAberta}`
        : pronto ? `w ${num(c?.umidade_media, CASAS.umidade)} %` : 'depois da seca',
    }] : []),
    { nome: 'Fotos e laudo', feita: ficha?.status === 'aprovado' },
  ];

  const indicadores: Indicador[] = [
    { rotulo: 'w · teor de umidade', chave: true, unidade: '%',
      valor: pronto ? num(c?.umidade_media, CASAS.umidade) : null,
      porque: aguardando > 0 ? 'Sai com todas as cápsulas pesadas secas.'
        : 'Sai com três determinações válidas.',
      como: <>w = (M<sub>1</sub> − M<sub>2</sub>) / (M<sub>2</sub> − M<sub>3</sub>) × 100,
        por cápsula; o resultado é a média das determinações válidas,
        expressa com 0,1 % (NBR 6457:2024).</> },
    { rotulo: 'Determinações válidas', valor: String(validas), nota: 'mínimo de 3' },
    { rotulo: 'Na estufa', valor: String(aguardando),
      nota: aguardando ? 'aguardando a pesagem seca' : undefined },
    { rotulo: 'Amplitude', unidade: 'p.p.',
      valor: pronto && validas >= 2 ? num(c?.umidade_amplitude, 2) : null,
      como: 'A maior menos a menor umidade entre as determinações válidas.' },
  ];

  return (
    <div className="tela trivial umidade ficha-carbon">
      <CabecalhoDaFicha id={id} sigla="UMID" titulo="Teor de umidade"
        ficha={ficha} normaDaTela="ABNT NBR 6457"
        tentativaAberta={d?.repeticaoAberta} />
      <FaixaDaFicha indicadores={indicadores} itens={d.pendencias}
        pendencias={d.pendenciasDetalhadas} />
      <EtapasDaFicha etapas={etapas} atual={etapa} irPara={irPara} />
      <OQueFalta d={d} id={id} carregar={t.carregar} />
      <RespostaDaAcao erro={t.erro} ok={t.ok ?? null} />
      <SoLeitura status={ficha?.status} />
      {repAberta && etapa <= 3 && (
        <AvisoDaRepeticaoAberta numero={d.repeticaoAberta!} interpreta={interpreta} />
      )}

      <fieldset className="so-leitura" disabled={congelado}>
      {etapa === 1 && (
        <BlocoDaFicha titulo="Condições do ensaio"
          sub="Como a amostra foi seca e em que balança se pesa. O laudo imprime a fração e a temperatura.">
          <div className="fe-miolo fe-campos">
            <Escolha rotulo="Fração ensaiada" valor={fracao} aoEscolher={setFracao}
              limpavel={false}
              opcoes={[
                { valor: 'total', texto: 'amostra total' },
                { valor: 'passante_2mm', texto: 'passante 2,0 mm' },
                { valor: 'passante_0_42mm', texto: 'passante 0,42 mm' },
              ]} />
            <Campo rotulo="Temperatura de secagem (°C)" inputMode="decimal"
              value={temp} onChange={(e) => setTemp(e.target.value)}
              erro={t.campoErro('temperaturaSecagem')}
              dica="105–110 °C é o padrão; solo orgânico seca mais frio, e o laudo diz." />
            <Campo rotulo="Permanência em estufa (h)" inputMode="decimal"
              value={horas} onChange={(e) => setHoras(e.target.value)}
              erro={t.campoErro('horasEstufa')} />
            <EscolhaDeBalanca rotulo="Balança da pesagem úmida" equipamentos={t.equip}
              valor={balancaId} aoEscolher={setBalancaId}
              erro={t.campoErro('balancaEquipamentoId')} />
          </div>
          <div className="fe-acoes-bloco">
            <button type="button" className="principal" disabled={t.salvando !== null}
              onClick={() => void t.gravar('cabecalho', () =>
                gravarUmidadeCabecalho(id, {
                  fracao: fracao ?? 'total',
                  temperaturaSecagem: paraNumero(temp),
                  horasEstufa: paraNumero(horas),
                  ...(balancaId ? { balancaEquipamentoId: balancaId } : {}),
                }))}>
              {t.salvando === 'cabecalho' ? 'Gravando…' : 'Gravar condições'}
            </button>
          </div>
        </BlocoDaFicha>
      )}

      {(etapa === 2 || etapa === 3) && repAberta && rep.carregando && (
        <Carregando texto="Lendo as linhas da repetição" />
      )}

      {etapa === 2 && !(repAberta && rep.carregando) && (
        <>
          <PesagemUmida key={repAberta ? `rep-${d.repeticaoAberta}` : 'vigente'}
            tipo="umidade" linhas={linhasDaBancada} minimo={3}
            opcoesCapsula={t.opcoes(CAT_CAPSULA)} equipamentos={t.equip}
            balanca={balancaDaBancada} salvando={t.salvando}
            euId={eu?.usuarioId} podeTravado={podeTravado} agora={agora}
            ocupadas={ocupadas} erroBalanca={t.campoErro('balancaEquipamentoId')}
            aoGravar={({ balancaEquipamentoId, linhas }) => t.gravar('linhas', () =>
              gravarUmidadeDeterminacoes(id, { balancaEquipamentoId, determinacoes: linhas }))}
            aoDescartar={(did, motivo) => t.gravar('descarte', () =>
              descartarUmidadeDeterminacao(id, did, motivo))}
            aoCorrigir={corr.abrir} />
          {bValidas > 0 && bValidas < 3 && (
            <Aviso tipo="atencao" titulo="Menos de três determinações válidas">
              O teor de umidade é a média de, no mínimo, três determinações por
              amostra (NBR 6457). Há {bValidas} — lance mais {3 - bValidas}; até lá o
              resultado não sai e o ensaio não vai para verificação.
            </Aviso>
          )}
        </>
      )}

      {etapa === 3 && !(repAberta && rep.carregando) && (
        <PesagemSeca tipo="umidade" linhas={linhasDaBancada} equipamentos={t.equip}
          balanca={balancaDaBancada} euId={eu?.usuarioId} podeTravado={podeTravado}
          agora={agora} aoPesado={t.carregar} aoCorrigir={corr.abrir}
          rodape={pronto && !repAberta ? (
            <p className="dd-media">Média das válidas: <b>{num(c?.umidade_media, CASAS.umidade)} %</b></p>
          ) : null} />
      )}

      {interpreta && etapa === 4 && (
        <>
          {/* A repetição na Interpretação (onda 323, RN-260, D7: só números). */}
          <RepeticaoDeCapsulas id={id} ficha="umidade" titulo="Teor de umidade"
            podeInterpretar={interpreta} congelado={congelado} aoMudar={t.carregar}
            resultado={RESULTADO_UMIDADE}
            dica="NBR 6457: a média de no mínimo três determinações, com 0,1 %"
            gravar={(corpo) => gravarUmidadeDeterminacoes(id, corpo)}
            descartar={(did, motivo) => descartarUmidadeDeterminacao(id, did, motivo)}>
            <ResultadoVigente resultado={RESULTADO_UMIDADE} f={d}
              linhas={d.determinacoes} colunas={COLUNAS_DE_CAPSULA}
              linha={(l) => linhaDeCapsula(l)} rotulo="Determinações do resultado" />
          </RepeticaoDeCapsulas>
          <TentativasAnteriores ensaioId={id} ficha="umidade" versao={t.versao}
            render={(r: any) => <TabelaDeLancamento rotulo="Determinações da tentativa"
              classe="capsulas fe-lanc repeticao-leitura" colunas={COLUNAS_DE_CAPSULA}
              linhas={(r?.determinacoes ?? []).map((l: any) => linhaDeCapsula(l))} />} />
          <TrilhaDoEnsaio ensaioId={id} versao={t.versao} />
        </>
      )}

      {etapa === etapaLaudo && <FotosELaudo id={id} />}
      </fieldset>

      <JanelaDeCorrecao alvo={corr.alvo} tipo="umidade" opcoesCapsula={t.opcoes(CAT_CAPSULA)}
        aoFechar={corr.fechar} aoGravado={() => void t.carregar()} />
      <RodapeDeEtapas etapas={etapas} atual={etapa} irPara={irPara}
        nota={notaDoRodape(t.ok)} />
    </div>
  );
}

// ══ 2 · limite de liquidez ════════════════════════════════════════════

export function LimiteLiquidez() {
  const { id = '' } = useParams();
  const { eu, pode } = useSessao();
  const t = useEnsaioTrivial(id, verLimiteLiquidez);
  const ficha = useFicha(id, 'NBR 6459', t.versao);
  const agora = useRelogio(t.d?.agora);
  const ocupadas = useCapsulasNaEstufa(t.versao);
  const corr = useCorrecao();
  /* R2 (onda 323): com a repetição aberta, as etapas de bancada listam e
     numeram as linhas DELA. */
  const rep = useLinhasDaRepeticao<any>(id, 'limite-liquidez', t.d?.repeticaoAberta, t.versao);
  const [aparelho, setAparelho] = useState<string | null>(null);
  const [naoLiquido, setNaoLiquido] = useState(false);
  const [preparacao, setPreparacao] = useState<string | null>('sem_secagem_previa');
  const [balancaId, setBalancaId] = useState('');

  const c = t.d?.cabecalho;
  useEffect(() => {
    setAparelho(c?.aparelho_equipamento_id ?? null);
    setNaoLiquido(Boolean(c?.nao_liquido));
    setPreparacao(c?.preparacao ?? 'sem_secagem_previa');
  }, [c?.aparelho_equipamento_id, c?.nao_liquido, c?.preparacao]);
  useEffect(() => { setBalancaId(t.d?.balanca?.id ?? ''); }, [t.d?.balanca?.id]);

  /*
   * A Interpretação existe só para quem tem `ensaio.interpretar` (RN-238):
   * «não pode ser vista e editada, apenas pelos usuários que tiverem a
   * permissão» (Humberto, 06/10/2026). Para os demais a ficha tem quatro
   * etapas, e o LL que aparece na faixa é o adotado.
   */
  const interpreta = pode('ensaio.interpretar');
  const pontos: number = c?.pontos ?? 0;
  const aguardando: number = c?.aguardando_seca ?? 0;
  const nl = Boolean(c?.nao_liquido);
  const temCondicoes = !!c?.aparelho_equipamento_id || nl;
  const temUmida = nl || (pontos >= 5 && !!t.d?.balanca);
  const temSeca = nl || (temUmida && aguardando === 0);
  const temInterpretacao = nl || (temSeca && c?.limite_liquidez != null && !c?.interpretacao_a_rever);
  const total = interpreta ? 5 : 4;
  const [etapa, irPara] = useEtapa(total,
    !temCondicoes ? 1 : !temUmida ? 2 : !temSeca ? 3
      : interpreta && !temInterpretacao ? 4 : total);

  if (t.erro && !t.d) return <ErroDaApi erro={t.erro} />;
  if (!t.d) return <Carregando texto="Abrindo o ensaio" />;
  const d = t.d;
  const par = d.par;
  const congelado = fichaCongelada(ficha?.status);
  const podeTravado = pode('ensaio.corrigir_lancamento');
  const pronto = !nl && pontos >= 5 && aguardando === 0 && c?.limite_liquidez != null;
  const etapaLaudo = interpreta ? 5 : 4;
  /* As linhas que as etapas de bancada mostram: as da repetição aberta (R2). */
  const repAberta = d.repeticaoAberta != null;
  const linhasDaBancada: any[] = repAberta ? (rep.atual?.pontos ?? []) : d.pontos;
  const balancaDaBancada = repAberta ? (rep.atual?.balanca ?? d.balanca) : d.balanca;
  /* Os resumos das etapas de bancada contam as MESMAS linhas que elas
     mostram: com a repetição aberta, as dela (revisão da Task 4). */
  const ca = rep.atual?.cabecalho;
  const bPontos: number = repAberta ? (ca?.pontos ?? 0) : pontos;
  const bPesados: number = repAberta ? (ca?.pesados ?? 0) : (c?.pesados ?? 0);
  const bAguardando: number = repAberta ? (ca?.aguardando_seca ?? 0) : aguardando;
  const bUmida = repAberta ? bPontos >= 5 && !!balancaDaBancada : temUmida;
  const bSeca = repAberta ? bUmida && bAguardando === 0 : temSeca;

  const etapas: Etapa[] = [
    { nome: 'Condições', feita: temCondicoes,
      resumo: nl ? 'declarado NL'
        : [c?.aparelho ? `Casagrande ${c.aparelho}` : null, balancaDaBancada?.codigo]
          .filter(Boolean).join(' · ') || 'a gravar' },
    { nome: 'Pesagem úmida', feita: bUmida,
      falta: !balancaDaBancada && bPontos > 0 && !nl,
      resumo: nl ? 'não se aplica'
        : `${bPontos} de 5 pontos${repAberta ? ` · tentativa ${d.repeticaoAberta}` : ''}` },
    { nome: 'Pesagem seca', feita: bSeca,
      resumo: nl ? 'não se aplica'
        : bPontos ? `${bPesados} de ${bPontos} pesados${repAberta ? ` · tentativa ${d.repeticaoAberta}` : ''}`
        : 'depois da úmida' },
    ...(interpreta ? [{
      nome: 'Interpretação', feita: temInterpretacao,
      falta: Boolean(c?.interpretacao_a_rever),
      resumo: nl ? 'não se aplica'
        : c?.interpretacao_a_rever ? 'a rever'
        : pronto ? `LL ${num(c?.limite_liquidez, CASAS.limite)} %${(c?.ajustes ?? 0) || c?.reta_modo === 'manual' ? ' · ajustado' : ''}`
        : 'depois da seca',
    }] : []),
    { nome: 'Fotos e laudo', feita: ficha?.status === 'aprovado' },
  ];

  const porqueLl = nl ? undefined
    : aguardando > 0 ? 'Sai com todos os pontos pesados secos.'
    : pontos < 5 ? 'Sai com no mínimo cinco pontos.'
    : c?.situacao ? rotulo(c.situacao) : undefined;
  const indicadores: Indicador[] = [
    { rotulo: 'LL', chave: true, unidade: nl ? undefined : '%',
      valor: nl ? 'NL' : pronto ? num(c.limite_liquidez, CASAS.limite) : null,
      porque: porqueLl,
      nota: pronto && interpreta && (c?.ajustes || c?.reta_modo === 'manual')
        ? `proposto ${num(c?.limite_liquidez_proposto, 2)}` : undefined,
      como: <>A umidade a 25 golpes, na reta w × log N ajustada aos pontos
        (ou adotada na interpretação); expresso em número inteiro
        (NBR 6459:2025).</> },
    { rotulo: 'Pontos válidos', valor: String(pontos), nota: 'mínimo de 5' },
    { rotulo: 'Na estufa', valor: String(aguardando),
      nota: aguardando ? 'aguardando a pesagem seca' : undefined },
    { rotulo: 'Golpes', valor: c?.golpe_minimo != null && c?.golpe_maximo != null
      ? `${c.golpe_minimo} a ${c.golpe_maximo}` : null },
    ...indicadoresDoPar(par, 'll'),
  ];

  return (
    <div className="tela trivial limite-liquidez ficha-carbon">
      <CabecalhoDaFicha id={id} sigla="LL" titulo="Limite de liquidez"
        ficha={ficha} normaDaTela="ABNT NBR 6459"
        tentativaAberta={d?.repeticaoAberta} />
      <FaixaDaFicha indicadores={indicadores} itens={d.pendencias}
        pendencias={d.pendenciasDetalhadas} />
      <EtapasDaFicha etapas={etapas} atual={etapa} irPara={irPara} />
      <OQueFalta d={d} id={id} carregar={t.carregar} />
      <RespostaDaAcao erro={t.erro} ok={t.ok ?? null} />
      <SoLeitura status={ficha?.status} />
      {d.repeticaoAberta != null && etapa <= 3 && (
        <AvisoDaRepeticaoAberta numero={d.repeticaoAberta} interpreta={interpreta} />
      )}

      <fieldset className="so-leitura" disabled={congelado}>
      {etapa === 1 && (
        <BlocoDaFicha titulo="Condições do ensaio"
          sub="O aparelho, a preparação da amostra e a balança — ou a declaração de que os pontos não se obtiveram.">
          <div className="fe-miolo fe-campos">
            <Escolha rotulo="Aparelho de Casagrande" valor={aparelho}
              aoEscolher={setAparelho} opcoes={t.opcoes(CAT_CASAGRANDE)}
              dica="Se o aparelho não aparecer, cadastre-o em Equipamentos." />
            <Escolha rotulo="Preparação da amostra" valor={preparacao}
              aoEscolher={setPreparacao} limpavel={false} opcoes={PREPARACAO} />
            <EscolhaDeBalanca rotulo="Balança da pesagem úmida" equipamentos={t.equip}
              valor={balancaId} aoEscolher={setBalancaId}
              erro={t.campoErro('balancaEquipamentoId')} />
            <label className="marcavel">
              <input type="checkbox" checked={naoLiquido}
                onChange={(e) => setNaoLiquido(e.target.checked)} />
              Não foi possível obter os pontos (declarar NL)
            </label>
            <p className="sub">
              Declarar aqui é registrar o que o técnico VIU na bancada — não é um
              resultado de cálculo (<code>PD-89</code>).
            </p>
          </div>
          <div className="fe-acoes-bloco">
            <button type="button" className="principal" disabled={t.salvando !== null}
              onClick={() => void t.gravar('cabecalho', () =>
                gravarLlCabecalho(id, {
                  aparelhoEquipamentoId: aparelho ?? undefined,
                  preparacao: preparacao ?? 'sem_secagem_previa',
                  naoLiquido,
                  ...(balancaId ? { balancaEquipamentoId: balancaId } : {}),
                }))}>
              {t.salvando === 'cabecalho' ? 'Gravando…' : 'Gravar condições'}
            </button>
          </div>
        </BlocoDaFicha>
      )}

      {(etapa === 2 || etapa === 3) && repAberta && rep.carregando && (
        <Carregando texto="Lendo as linhas da repetição" />
      )}

      {etapa === 2 && !(repAberta && rep.carregando) && (nl
        ? <Aviso tipo="info" titulo="Declarado NL">Sem pontos para lançar.</Aviso>
        : (
          <PesagemUmida key={repAberta ? `rep-${d.repeticaoAberta}` : 'vigente'}
            tipo="limite-liquidez" linhas={linhasDaBancada} minimo={5}
            opcoesCapsula={t.opcoes(CAT_CAPSULA)} equipamentos={t.equip}
            balanca={balancaDaBancada} salvando={t.salvando}
            euId={eu?.usuarioId} podeTravado={podeTravado} agora={agora}
            ocupadas={ocupadas} erroBalanca={t.campoErro('balancaEquipamentoId')}
            aoGravar={({ balancaEquipamentoId, linhas }) => t.gravar('linhas', () =>
              gravarLlPontos(id, { balancaEquipamentoId, pontos: linhas }))}
            aoDescartar={(pid, motivo) => t.gravar('descarte', () =>
              descartarLlPonto(id, pid, motivo))}
            aoCorrigir={corr.abrir} />
        ))}

      {etapa === 3 && !(repAberta && rep.carregando) && (nl
        ? <Aviso tipo="info" titulo="Declarado NL">Sem pontos para pesar.</Aviso>
        : (
          <>
            <PesagemSeca tipo="limite-liquidez" linhas={linhasDaBancada} equipamentos={t.equip}
              balanca={balancaDaBancada} euId={eu?.usuarioId} podeTravado={podeTravado}
              agora={agora} aoPesado={t.carregar} aoCorrigir={corr.abrir} />
            <BlocoDaFicha titulo="O par da amostra">
              <div className="fe-miolo">
                <ParDaAmostra par={par} id={id} telas={d.telas ?? []} lado="ll" />
              </div>
            </BlocoDaFicha>
          </>
        ))}

      {interpreta && etapa === 4 && (
        <>
          {/* A repetição na Interpretação (onda 323, RN-260): sem repetição
              aberta, a interpretação de sempre e o «Repetir o ensaio aqui». */}
          <RepeticaoLl id={id} podeInterpretar={interpreta} congelado={congelado}
            aoMudar={t.carregar}>
            {nl
              ? <Aviso tipo="info" titulo="Declarado NL">Não há reta para interpretar.</Aviso>
              : (
                <InterpretacaoLl id={id} d={d} congelado={congelado}
                  aoGravado={async () => { await t.carregar(); }} />
              )}
          </RepeticaoLl>
          <TentativasAnteriores ensaioId={id} ficha="limite-liquidez" versao={t.versao}
            render={(r: any) => <PontosDoLl pontos={r?.pontos ?? []} rotulo="Pontos da tentativa" />} />
          <TrilhaDoEnsaio ensaioId={id} versao={t.versao} />
        </>
      )}

      {etapa === etapaLaudo && <FotosELaudo id={id} nota={NOTA_DO_PAR} />}
      </fieldset>

      <JanelaDeCorrecao alvo={corr.alvo} tipo="limite-liquidez" opcoesCapsula={t.opcoes(CAT_CAPSULA)}
        aoFechar={corr.fechar} aoGravado={() => void t.carregar()} />
      <RodapeDeEtapas etapas={etapas} atual={etapa} irPara={irPara}
        nota={notaDoRodape(t.ok)} />
    </div>
  );
}

/**
 * A repetição aberta, nas etapas de bancada (onda 323): o lançamento novo
 * vai para ela, e quem lança precisa saber disso antes de gravar.
 */
export function AvisoDaRepeticaoAberta({ numero, interpreta }: { numero: number; interpreta: boolean }) {
  return (
    <Aviso tipo="atencao"
      titulo={`Há uma repetição aberta (tentativa ${numero}): os lançamentos vão para ela.`}>
      {interpreta
        ? 'As linhas abaixo são as da repetição; as do resultado inicial ficam na Interpretação, ao lado. O laudo continua com o resultado inicial até a repetição ser adotada.'
        : 'As linhas abaixo são as da repetição. Quem interpreta compara os dois resultados e decide se a adota; até lá, o laudo continua com o resultado inicial.'}
    </Aviso>
  );
}

// ══ 2b · a interpretação do LL (RN-238) ═══════════════════════════════

/** w = a·ln(N) + b por mínimos quadrados — a conta do banco (`fn_ll_reta`). */
export function retaMq(pts: Ponto[]): { a: number; b: number } | null {
  const bons = pts.filter((p) => p.x > 0 && Number.isFinite(p.y));
  const n = bons.length;
  if (n < 2) return null;
  const xs = bons.map((p) => Math.log(p.x));
  const ys = bons.map((p) => p.y);
  const sx = xs.reduce((s, v) => s + v, 0);
  const sy = ys.reduce((s, v) => s + v, 0);
  const sxx = xs.reduce((s, v) => s + v * v, 0);
  const sxy = xs.reduce((s, v, i) => s + v * ys[i], 0);
  const den = n * sxx - sx * sx;
  if (Math.abs(den) < 1e-12) return null;
  const a = (n * sxy - sx * sy) / den;
  return { a, b: (sy - a * sx) / n };
}

export function retaPorAlcas(p: Ponto, q: Ponto): { a: number; b: number } | null {
  if (p.x <= 0 || q.x <= 0 || p.x === q.x) return null;
  const a = (q.y - p.y) / (Math.log(q.x) - Math.log(p.x));
  return { a, b: p.y - a * Math.log(p.x) };
}

interface AjusteLocal { n: number | null; w: number | null; fora: boolean }

/**
 * A interpretação da reta do LL.
 *
 * «Na próxima aba (interpretação) é sugerida a reta, onde cabe fazer
 *  correções dos pontos (movimentando o ponto diretamente no gráfico ou em
 *  uma tabela auxiliar). Essa informação de ajuste não vai para o
 *  relatório final em PDF.» — Humberto, 06/10/2026.
 *
 * Duas mãos, a escolha dele («os dois»): deslocar o ponto (no gráfico ou
 * na tabela) e traçar a reta à mão pelas alças. O medido não muda nunca:
 * o adotado fica ao lado, com o motivo. A reta proposta (tracejada) é a dos
 * pontos medidos; a adotada (cheia) é a que vai para o laudo.
 */
function InterpretacaoLl({ id, d, congelado, aoGravado }: {
  id: string; d: any; congelado: boolean; aoGravado(): Promise<void>;
}) {
  const c = d.cabecalho;
  const pontos: any[] = useMemo(() => (d.pontos as any[]).filter(
    (p) => !descartada(p) && p.umidade != null), [d.pontos]);
  const aguardando: number = c?.aguardando_seca ?? 0;

  const [modo, setModo] = useState<'pontos' | 'manual'>('pontos');
  const [alcas, setAlcas] = useState<[Ponto, Ponto]>([{ x: 15, y: 0 }, { x: 35, y: 0 }]);
  const [ajustes, setAjustes] = useState<Record<string, AjusteLocal>>({});
  const [motivo, setMotivo] = useState('');
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [ok, setOk] = useState(false);

  /* Começa no que está gravado — e volta a ele a cada recarga. */
  useEffect(() => {
    setModo(c?.reta_modo === 'manual' ? 'manual' : 'pontos');
    if (c?.reta_modo === 'manual') {
      setAlcas([{ x: Number(c.reta_n1), y: Number(c.reta_w1) },
        { x: Number(c.reta_n2), y: Number(c.reta_w2) }]);
    }
    const m: Record<string, AjusteLocal> = {};
    for (const p of d.pontos as any[]) {
      if (p.ajustado) {
        m[p.id] = {
          n: p.golpes_adotados != null ? Number(p.golpes_adotados) : null,
          w: p.umidade_adotada != null ? Number(p.umidade_adotada) : null,
          fora: Boolean(p.fora_do_ajuste),
        };
      }
    }
    setAjustes(m);
    setMotivo(c?.interpretacao_motivo ?? '');
    setErro(null);
  }, [c?.reta_modo, c?.reta_n1, c?.reta_w1, c?.reta_n2, c?.reta_w2,
    c?.interpretacao_motivo, d.pontos]);

  const adotados = pontos.map((p) => {
    const a = ajustes[p.id];
    return {
      p,
      n: a?.n ?? Number(p.golpes),
      w: a?.w ?? Number(p.umidade),
      fora: a?.fora ?? false,
      movido: a != null && (a.n != null || a.w != null),
    };
  });
  const proposta = retaMq(pontos.map((p) => ({ x: Number(p.golpes), y: Number(p.umidade) })));
  const reta = modo === 'manual'
    ? retaPorAlcas(alcas[0], alcas[1])
    : retaMq(adotados.filter((x) => !x.fora).map((x) => ({ x: x.n, y: x.w })));
  const llAdotado = reta ? reta.a * Math.log(25) + reta.b : null;
  const llProposto = proposta ? proposta.a * Math.log(25) + proposta.b : null;
  const mexeu = modo === 'manual' || Object.keys(ajustes).length > 0;

  const todos: Ponto[] = useMemo(() => {
    const v: Ponto[] = [];
    for (const p of pontos) v.push({ x: Number(p.golpes), y: Number(p.umidade) });
    v.push({ x: 12, y: Math.min(...v.map((q) => q.y)) });
    v.push({ x: 42, y: Math.max(...v.map((q) => q.y)) });
    return v;
  }, [pontos]);
  const z = useJanela(todos, 0.15, { x: 'log' });

  const mudar = (pid: string, m: Partial<AjusteLocal>) => setAjustes((atual) => {
    const p = pontos.find((q) => q.id === pid);
    if (!p) return atual;
    const velho = atual[pid] ?? { n: null, w: null, fora: false };
    const novo = { ...velho, ...m };
    if (novo.n != null && novo.n === Number(p.golpes)) novo.n = null;
    if (novo.w != null && Math.abs(novo.w - Number(p.umidade)) < 0.005) novo.w = null;
    const resto = { ...atual };
    if (novo.n == null && novo.w == null && !novo.fora) delete resto[pid];
    else resto[pid] = novo;
    return resto;
  });

  const passarParaManual = () => {
    const r = reta ?? proposta;
    if (r) setAlcas([{ x: 15, y: r.a * Math.log(15) + r.b }, { x: 35, y: r.a * Math.log(35) + r.b }]);
    setModo('manual');
  };

  /* As alças arrastáveis: na reta à mão, as duas primeiras são as dela. */
  const arrastaveis: Ponto[] = [
    ...(modo === 'manual' ? alcas : []),
    ...adotados.map((x) => ({ x: x.n, y: x.w })),
  ];
  const aoArrastar = (p: Ponto, i: number) => {
    if (congelado) return;
    if (modo === 'manual' && i < 2) {
      const q: Ponto = { x: Math.max(1, p.x), y: Math.round(p.y * 100) / 100 };
      setAlcas((a) => (i === 0 ? [q, a[1]] : [a[0], q]));
      return;
    }
    const k = i - (modo === 'manual' ? 2 : 0);
    const alvo = adotados[k];
    if (!alvo) return;
    mudar(alvo.p.id, {
      n: Math.min(60, Math.max(5, Math.round(p.x))),
      w: Math.round(p.y * 100) / 100,
    });
  };

  const voltarAProposta = () => {
    setModo('pontos'); setAjustes({}); setMotivo(''); setOk(false);
  };

  const falta = aguardando > 0 ? 'Há pontos na estufa.'
    : !reta ? 'A reta adotada não está determinada.'
    : mexeu && motivo.trim().length < 5 ? 'Diga por que a reta foi ajustada (motivo).'
    : null;

  const gravar = async () => {
    if (falta || gravando || congelado) return;
    setGravando(true); setErro(null); setOk(false);
    try {
      await gravarInterpretacaoLl(id, {
        modo,
        ...(modo === 'manual' ? { alcas: { n1: alcas[0].x, w1: alcas[0].y, n2: alcas[1].x, w2: alcas[1].y } } : {}),
        ajustes: Object.entries(ajustes).map(([pontoId, a]) => ({
          pontoId, golpesAdotados: a.n, umidadeAdotada: a.w, foraDoAjuste: a.fora })),
        ...(mexeu ? { motivo: motivo.trim() } : {}),
      });
      setOk(true);
      await aoGravado();
    } catch (e) { setErro(e); } finally { setGravando(false); }
  };

  if (aguardando > 0) {
    return (
      <Aviso tipo="atencao" titulo={`${plural(aguardando, 'ponto está', 'pontos estão')} na estufa`}>
        A interpretação se faz com todos os pontos pesados secos (RN-235).
      </Aviso>
    );
  }
  if (pontos.length < 2) {
    return <Aviso tipo="info" titulo="Sem pontos para traçar">A reta aparece com dois pontos pesados.</Aviso>;
  }

  const ponto25 = llAdotado != null ? { x: 25, y: llAdotado } : null;

  return (
    <>
      {c?.interpretacao_a_rever && (
        <Aviso tipo="atencao" titulo="A interpretação precisa ser revista">
          A interpretação gravada deixou de valer: {c.interpretacao_a_rever}. Confira os
          pontos e grave de novo.
        </Aviso>
      )}
      <div className="dd-ajuste">
        <section className="bloco fe-bloco fe-grafico dd-grafico">
          <header className="fe-bloco-topo">
            <h2><span>Reta w × log N</span></h2>
            <Zoom z={z} />
          </header>
          <div className="fe-miolo">
            <Plano janela={z.janela} escalaX="log" aoJanela={z.definir}
              tituloX="Número de golpes — N (escala log)" tituloY="w (%)"
              alcas={arrastaveis} aoArrastar={aoArrastar} desabilitado={congelado}>
              {({ px }) => (
                <>
                  {proposta && (
                    <Reta a={{ x: 10, y: proposta.a * Math.log(10) + proposta.b }}
                      b={{ x: 100, y: proposta.a * Math.log(100) + proposta.b }}
                      px={px} classe="reta dd-proposta" />
                  )}
                  {reta && (
                    <Reta a={{ x: 10, y: reta.a * Math.log(10) + reta.b }}
                      b={{ x: 100, y: reta.a * Math.log(100) + reta.b }} px={px} />
                  )}
                  {ponto25 && (
                    <>
                      <Segmento a={{ x: 25, y: z.janela.y0 }} b={ponto25} px={px} classe="reta auxiliar" />
                      <Segmento a={{ x: z.janela.x0, y: ponto25.y }} b={ponto25} px={px} classe="reta auxiliar" />
                    </>
                  )}
                  {adotados.map((x) => {
                    const medido = px({ x: Number(x.p.golpes), y: Number(x.p.umidade) });
                    const q = px({ x: x.n, y: x.w });
                    return (
                      <g key={x.p.id}>
                        {x.movido && (
                          <>
                            <line className="dd-rastro" x1={medido.x} y1={medido.y} x2={q.x} y2={q.y} />
                            <circle className="dd-fantasma" cx={medido.x} cy={medido.y} r={5} />
                          </>
                        )}
                        <circle className={x.fora ? 'ponto dd-fora' : x.movido ? 'ponto dd-movido' : 'ponto'}
                          cx={q.x} cy={q.y} r={5.5} />
                        <text className="dd-rotulo-ponto" x={q.x + 8} y={q.y - 7}>{x.p.ordem}</text>
                      </g>
                    );
                  })}
                  {modo === 'manual' && alcas.map((h, k) => (
                    <Alca key={k} p={h} px={px} rotulo={k === 0 ? 'alça 1' : 'alça 2'} />
                  ))}
                  {ponto25 && (
                    <g className="achado">
                      <circle cx={px(ponto25).x} cy={px(ponto25).y} r={6} />
                      <text x={px(ponto25).x + 10} y={px(ponto25).y + 16}>
                        LL = {num(llAdotado, CASAS.limite)} %
                      </text>
                    </g>
                  )}
                </>
              )}
            </Plano>
            <p className="fe-legenda">
              <span><i className="l-reta" />reta adotada</span>
              <span><i className="l-aux dd-l-proposta" />reta proposta (pontos medidos)</span>
              <span><i className="dd-l-movido" />ponto deslocado</span>
              <span><i className="dd-l-fantasma" />onde foi medido</span>
            </p>
            <p className="sub dd-instrucao">
              Arraste um ponto para deslocá-lo (os golpes andam de um em um); na reta à mão,
              arraste as alças. A roda do mouse aproxima; o botão do meio arrasta o gráfico.
            </p>
          </div>
        </section>

        <section className="bloco fe-bloco dd-painel">
          <header className="fe-bloco-topo"><h2><span>A reta</span></h2></header>
          <div className="fe-miolo">
            <Escolha rotulo="Como a reta é traçada" limpavel={false}
              valor={modo} desabilitado={congelado}
              aoEscolher={(v) => (v === 'manual' ? passarParaManual() : setModo('pontos'))}
              opcoes={[
                { valor: 'pontos', texto: 'Pelos pontos adotados' },
                { valor: 'manual', texto: 'À mão (alças)' },
              ]} />
            <dl className="fe-mosaico dd-mosaico">
              <div><dt>LL proposto</dt>
                <dd>{llProposto != null ? `${num(llProposto, CASAS.limite)} %` : '—'}
                  <small> · {llProposto != null ? num(llProposto, 2) : '—'}</small></dd></div>
              <div className="destaque"><dt>LL adotado</dt>
                <dd>{llAdotado != null ? `${num(llAdotado, CASAS.limite)} %` : '—'}
                  <small> · {llAdotado != null ? num(llAdotado, 2) : '—'}</small></dd></div>
            </dl>
            <Area rotulo={mexeu ? 'Motivo do ajuste' : 'Motivo do ajuste (só se ajustar)'}
              rows={3} value={motivo} disabled={congelado}
              onChange={(e) => setMotivo(e.target.value)}
              dica="Fica na trilha e aparece para quem verifica. Não sai no laudo." />
            {c?.interpretado_em && (
              <p className="sub">
                Gravada por {c.interpretado_por_nome ?? '—'} em {quando(c.interpretado_em)}
                {c.reta_modo === 'manual' ? ' · reta à mão'
                  : c.ajustes ? ` · ${plural(c.ajustes, 'ponto ajustado', 'pontos ajustados')}` : ' · reta proposta'}.
              </p>
            )}
            <RespostaDaAcao erro={erro} ok={ok ? 'Interpretação gravada.' : null} />
            <div className="dd-botoes">
              <button type="button" className="principal"
                aria-disabled={falta !== null || gravando || congelado}
                title={falta ?? undefined} onClick={() => void gravar()}>
                {gravando ? 'Gravando…' : 'Gravar interpretação'}
              </button>
              <button type="button" className="botao discreto" disabled={congelado || !mexeu}
                onClick={voltarAProposta}>
                Voltar à reta proposta
              </button>
            </div>
          </div>
        </section>
      </div>

      <BlocoDaFicha titulo="Tabela auxiliar"
        selo={Object.keys(ajustes).length ? <Selo tom="atencao">{plural(Object.keys(ajustes).length, 'ajuste', 'ajustes')}</Selo> : undefined}
        sub="O mesmo que o gráfico, em números: digite os golpes ou o w adotado de um ponto, ou tire-o do ajuste. O medido nunca muda.">
        <div className="fe-tabela">
          <TabelaDeLancamento rotulo="Pontos adotados" classe="fe-lanc dd-auxiliar"
            colunas={[
              { texto: 'Ponto' }, { texto: 'Cápsula' },
              { texto: 'N medido', num: true }, { texto: 'N adotado', num: true },
              { texto: 'w medido (%)', num: true }, { texto: 'w adotado (%)', num: true },
              { texto: 'No ajuste' },
            ]}
            linhas={adotados.map((x) => ({
              chave: x.p.id,
              classe: ajustes[x.p.id] ? 'depurada' : undefined,
              titulo: <>Ponto {x.p.ordem} · <span className="cod">{x.p.capsula}</span></>,
              resultado: <span className="n">{num(x.w, 2)} %</span>,
              semCartao: [0, 1],
              celulas: [
                x.p.ordem,
                <span className="cod">{x.p.capsula}</span>,
                x.p.golpes,
                <CampoNumerico rotulo={`N adotado do ponto ${x.p.ordem}`} rotuloOculto inteiro
                  disabled={congelado} value={String(x.n)}
                  onChange={(e) => {
                    const v = paraNumero(e.target.value);
                    if (v != null) mudar(x.p.id, { n: Math.round(v) });
                  }} />,
                num(x.p.umidade, 2),
                <CampoNumerico rotulo={`w adotado do ponto ${x.p.ordem}`} rotuloOculto
                  disabled={congelado} defaultValue={paraCampo(x.w)} key={`${x.p.id}-${x.w}`}
                  onBlur={(e) => {
                    const v = paraNumero(e.target.value);
                    if (v != null) mudar(x.p.id, { w: Math.round(v * 100) / 100 });
                  }} />,
                <label className="marcavel dd-no-ajuste">
                  <input type="checkbox" checked={!x.fora} disabled={congelado}
                    onChange={(e) => mudar(x.p.id, { fora: !e.target.checked })} />
                  {x.fora ? 'fora' : 'sim'}
                </label>,
              ],
            }))} />
        </div>
      </BlocoDaFicha>
      <Aviso tipo="info" titulo="O que vai para o laudo">
        Os pontos e a reta adotados e o LL — sem as alças, sem o ponto «onde foi
        medido» e sem o motivo. A tabela de cápsulas do laudo continua com as massas
        pesadas e o w medido. O ajuste, quem fez e o motivo ficam na trilha e aqui.
      </Aviso>
    </>
  );
}

// ══ 3 · limite de plasticidade ════════════════════════════════════════

/**
 * A comparação da repetição do LP: o número inteiro do servidor; NP é
 * palavra, e «refazer» (menos de três dentro dos 5 %) não tem número.
 */
const RESULTADO_LP: ResultadoNumerico = {
  rotulo: 'LP', unidade: '%', casas: CASAS.limite, unidadeDaDiferenca: 'pontos',
  texto: (f) => (f?.cabecalho?.nao_plastico ? 'NP' : null),
  valor: (f) => {
    const c = f?.cabecalho;
    if (!c || c.situacao === 'refazer_ensaio' || Number(c.aguardando_seca ?? 0) > 0) return null;
    return c.limite_plasticidade != null ? Number(c.limite_plasticidade) : null;
  },
  nota: (f, lado) => {
    const c = f?.cabecalho;
    if (c?.nao_plastico) return 'declarado NP';
    const n = Number(c?.determinacoes ?? 0);
    const espera = Number(c?.aguardando_seca ?? 0);
    return plural(n, 'determinação', 'determinações')
      + (espera ? ` · ${espera} na estufa`
        : n && c?.sobreviventes != null ? ` · ${c.sobreviventes} dentro dos 5 %` : '')
      + (lado === 'atual' ? ' (mínimo 3)' : '');
  },
  semDiferenca: 'sai com três determinações dentro dos 5 %',
};

/** A depurada dos 5 % de uma leitura da ficha — pela lista do servidor. */
function foraDosCincoPorCento(l: any, f: any): boolean {
  const c = f?.cabecalho;
  if (!c || c.nao_plastico || Number(c.aguardando_seca ?? 0) > 0 || !Number(c.determinacoes ?? 0)) {
    return false;
  }
  if (descartada(l) || l.umidade == null) return false;
  const aceitas: number[] = (c.umidades_aceitas ?? []).map(Number);
  return !aceitas.some((a) => Math.abs(a - Number(l.umidade)) < 1e-9);
}

export function LimitePlasticidade() {
  const { id = '' } = useParams();
  const { eu, pode } = useSessao();
  const t = useEnsaioTrivial(id, verLimitePlasticidade);
  const ficha = useFicha(id, 'NBR 7180', t.versao);
  const agora = useRelogio(t.d?.agora);
  const ocupadas = useCapsulasNaEstufa(t.versao);
  const corr = useCorrecao();
  /* R2 (onda 323): com a repetição aberta, a bancada é a da repetição. */
  const rep = useLinhasDaRepeticao<any>(id, 'limite-plasticidade', t.d?.repeticaoAberta, t.versao);
  const [naoPlastico, setNaoPlastico] = useState(false);
  const [preparacao, setPreparacao] = useState<string | null>('sem_secagem_previa');
  const [balancaId, setBalancaId] = useState('');

  const c = t.d?.cabecalho;
  useEffect(() => {
    setNaoPlastico(Boolean(c?.nao_plastico));
    setPreparacao(c?.preparacao ?? 'sem_secagem_previa');
  }, [c?.nao_plastico, c?.preparacao]);
  useEffect(() => { setBalancaId(t.d?.balanca?.id ?? ''); }, [t.d?.balanca?.id]);

  const dets: number = c?.determinacoes ?? 0;
  const aguardando: number = c?.aguardando_seca ?? 0;
  const np = Boolean(c?.nao_plastico);
  const temCondicoes = c != null;
  const temUmida = np || (dets >= 3 && !!t.d?.balanca);
  const temSeca = np || (temUmida && aguardando === 0);
  const interpreta = pode('ensaio.interpretar');
  const total = interpreta ? 5 : 4;
  const [etapa, irPara] = useEtapa(total,
    !temCondicoes ? 1 : !temUmida ? 2 : !temSeca ? 3 : total);

  if (t.erro && !t.d) return <ErroDaApi erro={t.erro} />;
  if (!t.d) return <Carregando texto="Abrindo o ensaio" />;
  const d = t.d;
  const congelado = fichaCongelada(ficha?.status);
  const podeTravado = pode('ensaio.corrigir_lancamento');
  const pronto = !np && aguardando === 0 && dets > 0;
  const etapaLaudo = total;

  /* As linhas e os resumos das etapas de bancada: os da repetição aberta (R2). */
  const repAberta = d.repeticaoAberta != null;
  const linhasDaBancada: any[] = repAberta ? (rep.atual?.determinacoes ?? []) : d.determinacoes;
  const balancaDaBancada = repAberta ? (rep.atual?.balanca ?? d.balanca) : d.balanca;
  const ca = repAberta ? rep.atual?.cabecalho : c;
  const bDets: number = ca?.determinacoes ?? 0;
  const bAguardando: number = ca?.aguardando_seca ?? 0;
  const bUmida = repAberta ? np || (bDets >= 3 && !!balancaDaBancada) : temUmida;
  const bSeca = repAberta ? np || (bUmida && bAguardando === 0) : temSeca;
  const bPronto = !np && bAguardando === 0 && bDets > 0;
  const naTentativa = repAberta ? ` · tentativa ${d.repeticaoAberta}` : '';

  /* Quem a depuração dos 5 % deixou de fora — o servidor manda a lista. */
  const aceitas: number[] = (ca?.umidades_aceitas ?? []).map(Number);
  const foraDaMedia = (l: any) => {
    if (!bPronto || descartada(l) || l.umidade == null) return false;
    return !aceitas.some((a) => Math.abs(a - Number(l.umidade)) < 1e-9);
  };

  const etapas: Etapa[] = [
    { nome: 'Condições', feita: temCondicoes,
      resumo: np ? 'declarado NP' : temCondicoes
        ? [rotulo(c?.preparacao), balancaDaBancada?.codigo].filter(Boolean).join(' · ') : 'a gravar' },
    { nome: 'Pesagem úmida', feita: bUmida,
      falta: !balancaDaBancada && bDets > 0 && !np,
      resumo: np ? 'não se aplica'
        : `${plural(bDets, 'determinação', 'determinações')}${naTentativa}` },
    { nome: 'Pesagem seca', feita: bSeca,
      resumo: np ? 'não se aplica'
        : bDets ? `${ca?.pesadas ?? 0} de ${bDets} pesadas`
          + (bPronto && ca?.depuradas ? ` · ${ca.depuradas} fora dos 5 %` : '') + naTentativa
          : 'depois da úmida' },
    ...(interpreta ? [{
      nome: 'Interpretação', feita: (np || pronto) && !repAberta,
      resumo: repAberta ? `repetindo · tentativa ${d.repeticaoAberta}`
        : np ? 'declarado NP'
        : pronto && c?.limite_plasticidade != null && c?.situacao !== 'refazer_ensaio'
          ? `LP ${num(c.limite_plasticidade, CASAS.limite)} %` : 'depois da seca',
    }] : []),
    { nome: 'Fotos e laudo', feita: ficha?.status === 'aprovado' },
  ];

  const indicadores: Indicador[] = [
    { rotulo: 'LP', chave: true, unidade: np ? undefined : '%',
      valor: np ? 'NP'
        : !pronto ? null
        : c?.situacao === 'refazer_ensaio' ? 'refazer'
        : c?.limite_plasticidade != null ? num(c.limite_plasticidade, CASAS.limite) : null,
      porque: aguardando > 0 ? 'Sai com todas as cápsulas pesadas secas.' : undefined,
      como: <>A média das umidades que ficam a até 5 % dela — as de fora
        são depuradas, e são precisas três; expresso em número inteiro
        (NBR 7180:2025).</> },
    { rotulo: 'Determinações', valor: String(dets) },
    { rotulo: 'Na estufa', valor: String(aguardando),
      nota: aguardando ? 'aguardando a pesagem seca' : undefined },
    { rotulo: 'Fora dos 5 %', valor: pronto ? String(c?.depuradas ?? 0) : null },
    ...indicadoresDoPar(d.par, 'lp'),
  ];

  return (
    <div className="tela trivial limite-plasticidade ficha-carbon">
      <CabecalhoDaFicha id={id} sigla="LP" titulo="Limite de plasticidade"
        ficha={ficha} normaDaTela="ABNT NBR 7180"
        tentativaAberta={d?.repeticaoAberta} />
      <FaixaDaFicha indicadores={indicadores} itens={d.pendencias}
        pendencias={d.pendenciasDetalhadas} />
      <EtapasDaFicha etapas={etapas} atual={etapa} irPara={irPara} />
      <OQueFalta d={d} id={id} carregar={t.carregar} />
      <RespostaDaAcao erro={t.erro} ok={t.ok ?? null} />
      <SoLeitura status={ficha?.status} />
      {repAberta && etapa <= 3 && (
        <AvisoDaRepeticaoAberta numero={d.repeticaoAberta!} interpreta={interpreta} />
      )}

      <fieldset className="so-leitura" disabled={congelado}>
      {etapa === 1 && (
        <BlocoDaFicha titulo="Condições do ensaio"
          sub="A preparação da amostra e a balança — ou a declaração de NP.">
          <div className="fe-miolo fe-campos">
            <Escolha rotulo="Preparação da amostra" valor={preparacao}
              aoEscolher={setPreparacao} limpavel={false} opcoes={PREPARACAO} />
            <EscolhaDeBalanca rotulo="Balança da pesagem úmida" equipamentos={t.equip}
              valor={balancaId} aoEscolher={setBalancaId}
              erro={t.campoErro('balancaEquipamentoId')} />
            <label className="marcavel">
              <input type="checkbox" checked={naoPlastico}
                onChange={(e) => setNaoPlastico(e.target.checked)} />
              O cilindro de 3 mm não se formou — declarar NP
            </label>
            <p className="sub">
              NP é observação de bancada, não resultado de cálculo
              (<code>PD-89</code>). Um ensaio com determinações lançadas não aceita a
              declaração: descarte-as antes, com motivo (<code>RN-162</code>).
            </p>
          </div>
          <div className="fe-acoes-bloco">
            <button type="button" className="principal" disabled={t.salvando !== null}
              onClick={() => void t.gravar('cabecalho', () =>
                gravarLpCabecalho(id, {
                  naoPlastico,
                  preparacao: preparacao ?? 'sem_secagem_previa',
                  ...(balancaId ? { balancaEquipamentoId: balancaId } : {}),
                }))}>
              {t.salvando === 'cabecalho' ? 'Gravando…' : 'Gravar condições'}
            </button>
          </div>
        </BlocoDaFicha>
      )}

      {(etapa === 2 || etapa === 3) && repAberta && rep.carregando && (
        <Carregando texto="Lendo as linhas da repetição" />
      )}

      {etapa === 2 && !(repAberta && rep.carregando) && (np
        ? <Aviso tipo="info" titulo="Declarado NP">Sem determinações para lançar.</Aviso>
        : (
          <PesagemUmida key={repAberta ? `rep-${d.repeticaoAberta}` : 'vigente'}
            tipo="limite-plasticidade" linhas={linhasDaBancada} minimo={3}
            opcoesCapsula={t.opcoes(CAT_CAPSULA)} equipamentos={t.equip}
            balanca={balancaDaBancada} salvando={t.salvando}
            euId={eu?.usuarioId} podeTravado={podeTravado} agora={agora}
            ocupadas={ocupadas} erroBalanca={t.campoErro('balancaEquipamentoId')}
            aoGravar={({ balancaEquipamentoId, linhas }) => t.gravar('linhas', () =>
              gravarLpDeterminacoes(id, { balancaEquipamentoId, determinacoes: linhas }))}
            aoDescartar={(did, motivo) => t.gravar('descarte', () =>
              descartarLpDeterminacao(id, did, motivo))}
            aoCorrigir={corr.abrir} />
        ))}

      {etapa === 3 && !(repAberta && rep.carregando) && (np
        ? <Aviso tipo="info" titulo="Declarado NP">Sem determinações para pesar.</Aviso>
        : (
          <>
            <PesagemSeca tipo="limite-plasticidade" linhas={linhasDaBancada}
              equipamentos={t.equip} balanca={balancaDaBancada} euId={eu?.usuarioId}
              podeTravado={podeTravado} agora={agora} aoPesado={t.carregar}
              aoCorrigir={corr.abrir} foraDaMedia={foraDaMedia} />
            {bPronto && ca?.situacao === 'refazer_ensaio' && (
              <Aviso tipo="atencao" titulo="Menos de três determinações válidas">
                Depois da depuração dos 5 % sobraram {ca?.sobreviventes ?? 0}. A NBR 7180
                exige três — o resultado não é conclusivo.
              </Aviso>
            )}
            <BlocoDaFicha titulo="O par da amostra">
              <div className="fe-miolo">
                <ParDaAmostra par={d.par} id={id} telas={d.telas ?? []} lado="lp" />
              </div>
            </BlocoDaFicha>
          </>
        ))}

      {interpreta && etapa === 4 && (
        <>
          {/* A repetição na Interpretação (onda 323, RN-260, D7: só números). */}
          <RepeticaoDeCapsulas id={id} ficha="limite-plasticidade" titulo="Limite de plasticidade"
            podeInterpretar={interpreta} congelado={congelado} aoMudar={t.carregar}
            resultado={RESULTADO_LP}
            dica="NBR 7180: a média das umidades a até 5 % dela, com três no mínimo, em número inteiro"
            gravar={(corpo) => gravarLpDeterminacoes(id, corpo)}
            descartar={(did, motivo) => descartarLpDeterminacao(id, did, motivo)}
            foraDaMedia={foraDosCincoPorCento}
            /* NP mora no cabeçalho, que é um só para todas as tentativas:
               declarado, vale também para a repetição (RN-162). */
            semLancar={() => np}
            avisos={() => (np ? <AvisoDoNp /> : null)}>
            {np
              ? <Aviso tipo="info" titulo="Declarado NP">Não há determinação para interpretar.</Aviso>
              : (
                <ResultadoVigente resultado={RESULTADO_LP} f={d}
                  linhas={d.determinacoes} colunas={COLUNAS_DE_CAPSULA}
                  linha={(l, f) => linhaDeCapsula(l, foraDosCincoPorCento(l, f))}
                  rotulo="Determinações do resultado" />
              )}
          </RepeticaoDeCapsulas>
          <TentativasAnteriores ensaioId={id} ficha="limite-plasticidade" versao={t.versao}
            render={(r: any) => <TabelaDeLancamento rotulo="Determinações da tentativa"
              classe="capsulas fe-lanc repeticao-leitura" colunas={COLUNAS_DE_CAPSULA}
              linhas={(r?.determinacoes ?? []).map((l: any) =>
                linhaDeCapsula(l, foraDosCincoPorCento(l, r)))} />} />
          <TrilhaDoEnsaio ensaioId={id} versao={t.versao} />
        </>
      )}

      {etapa === etapaLaudo && <FotosELaudo id={id} nota={NOTA_DO_PAR} />}
      </fieldset>

      <JanelaDeCorrecao alvo={corr.alvo} tipo="limite-plasticidade"
        opcoesCapsula={t.opcoes(CAT_CAPSULA)}
        aoFechar={corr.fechar} aoGravado={() => void t.carregar()} />
      <RodapeDeEtapas etapas={etapas} atual={etapa} irPara={irPara}
        nota={notaDoRodape(t.ok)} />
    </div>
  );
}

// ══ o par da amostra ══════════════════════════════════════════════════

/** O outro lado e o IP, na faixa — o IP só existe no par. */
function indicadoresDoPar(par: any, lado: 'll' | 'lp'): Indicador[] {
  if (!par) return [];
  const outro: Indicador = lado === 'll'
    ? { rotulo: 'LP · da outra ficha', unidade: par.nao_plastico ? undefined : '%',
        valor: par.nao_plastico ? 'NP' : par.limite_plasticidade != null
          ? num(par.limite_plasticidade, CASAS.limite) : null,
        porque: 'O LP ainda não foi lançado.' }
    : { rotulo: 'LL · da outra ficha', unidade: par.nao_liquido ? undefined : '%',
        valor: par.nao_liquido ? 'NL' : par.limite_liquidez != null
          ? num(par.limite_liquidez, CASAS.limite) : null,
        porque: 'O LL ainda não foi lançado.' };
  const ip: Indicador = {
    rotulo: 'IP', unidade: par.nao_liquido || par.nao_plastico ? undefined : '%',
    valor: par.nao_liquido || par.nao_plastico ? 'NP'
      : par.situacao === 'ip_negativo' ? null
      : par.indice_plasticidade != null
        ? num(par.indice_plasticidade, CASAS.limite) : null,
    porque: par.situacao === 'ip_negativo'
      ? 'LP maior que LL — confira as pesagens.'
      : 'Só existe com os dois lados.',
    como: <>IP = LL − LP, com os dois já expressos em número inteiro
      (NBR 7180:2025, 5.2.1).</>,
  };
  return [outro, ip];
}

/**
 * O outro lado do par, e o IP.
 *
 * Existe nas duas telas porque a pergunta «e o IP?» é a primeira que o
 * técnico faz depois de fechar um dos dois — e a resposta honesta, quando
 * falta o outro lado, é dizer qual falta e oferecer o caminho.
 */
function ParDaAmostra({ par, id, telas, lado }: {
  par: any; id: string; telas: string[]; lado: 'll' | 'lp';
}) {
  if (!par) return null;
  /*
   * O LA lança os dois lados no MESMO ensaio (0077): «falta o LP» quer
   * dizer «vá à outra ficha deste ensaio», e não «programe outro». A
   * tela sabe pelas `telas` do método (184).
   */
  const outraFicha = (tela: string) => telas.includes(tela)
    ? <Link to={`/ensaio/${id}/${tela}`}>a outra ficha deste ensaio</Link>
    : null;
  if (par.situacao === 'sem_lp' && lado === 'lp') {
    return (
      <Aviso tipo="info" titulo="O LP ainda não foi lançado">
        Grave as condições e as determinações — o índice de plasticidade aparece
        aqui quando os dois lados existirem.
      </Aviso>
    );
  }
  if (par.situacao === 'sem_ll' && lado === 'll') {
    return (
      <Aviso tipo="info" titulo="O LL ainda não foi lançado">
        Grave as condições e os pontos — o índice de plasticidade aparece aqui
        quando os dois lados existirem.
      </Aviso>
    );
  }
  if (par.situacao === 'sem_lp') {
    const outra = outraFicha('limite-plasticidade');
    return (
      <Aviso tipo="info" titulo="Falta o limite de plasticidade">
        O índice de plasticidade só existe com os dois lados.{' '}
        {outra
          ? <>Lance o LP em {outra} — o ensaio só fecha com os dois.</>
          : <>Programe o LP desta amostra em <Link to="/programar">Programar ensaios</Link>.</>}
      </Aviso>
    );
  }
  if (par.situacao === 'sem_ll') {
    const outra = outraFicha('limite-liquidez');
    return (
      <Aviso tipo="info" titulo="Falta o limite de liquidez">
        O índice de plasticidade só existe com os dois lados.{' '}
        {outra
          ? <>Lance o LL em {outra} — o ensaio só fecha com os dois.</>
          : <>Programe o LL desta amostra em <Link to="/programar">Programar ensaios</Link>.</>}
      </Aviso>
    );
  }
  if (par.situacao === 'ip_negativo') {
    return (
      <Aviso tipo="atencao" titulo="LP maior que LL">
        O limite de plasticidade resultou maior que o de liquidez, o que não é
        fisicamente possível. Confira as pesagens antes de aprovar — o índice de
        plasticidade não foi calculado.
      </Aviso>
    );
  }
  return (
    <dl className="resumo par fe-mosaico">
      <div><dt>LL</dt><dd>{par.nao_liquido ? 'NL'
        : `${num(par.limite_liquidez, CASAS.limite)} %`}</dd></div>
      <div><dt>LP</dt><dd>{par.nao_plastico ? 'NP'
        : `${num(par.limite_plasticidade, CASAS.limite)} %`}</dd></div>
      <div className="destaque"><dt>IP</dt>
        <dd>{par.nao_liquido || par.nao_plastico ? 'NP'
          : `${num(par.indice_plasticidade, CASAS.limite)} %`}</dd></div>
    </dl>
  );
}

/** Para a Estufa: a mesma mensagem de erro curta que as fichas usam. */
export const textoDoErro = (e: unknown) => mensagemDoErro(e);

/* O quadro de um dia só, que a massa específica ainda usa (onda 290). */
export { QuadroCapsulas } from './QuadroCapsulas';
